import { bundleStatus, inclusionsFor } from './eg-bundle-nudge.js';
import { closeModalWithExit } from './eg-modal-exit.js';
import {
  ADDITIVE,
  AUTHORITATIVE,
  buildReadability,
  hydrate,
  pendingUnits,
  reconcileStatus,
} from './eg-cart-reconcile.js';
import {
  createConfigurator,
  formatMoney,
  generateBundleId,
  parseIdMap,
} from './eg-configurator-state.js';

/**
 * Planner configurator — DOM WIRING and the CONVERGE-COMMIT LOOP
 * (U6 / R5, R6, R8, R9, R10, R11, R12, R14, R15, R20, R21, R22, R26;
 * KTD-A, KTD-B, KTD-H).
 *
 * Every selection and pricing rule lives in eg-configurator-state.js; every
 * cart-diff decision lives in eg-cart-reconcile.js; bundle counting lives in
 * eg-bundle-nudge.js. This file renders those three and writes the cart.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE LOOP IS THE DANGEROUS PART OF THIS FILE, SO IT COMES FIRST AND IT IS PURE.
 *
 * `nextOp` is a pure function: it cannot observe whether the write it proposed
 * actually landed, so TERMINATION IS THIS FILE'S PROPERTY, not the module's
 * (KTD-A). `convergeCommit` below is therefore exported and DOM-free, and it is
 * driven against a stubbed cart in tests/eg-configurator-loop.test.js — where a
 * cart that freezes, merges, refuses or moves under a write can be simulated.
 * A green reconcile module driven by wrong loop mechanics is still a
 * cart-destroying bug, which is why the loop landed before any visuals.
 *
 * THE NINE CALLER OBLIGATIONS the reconcile module's header states, and where
 * each one is discharged (two independent adversarial reviews proved, by
 * execution, that skipping any of them re-opens a money-losing bug):
 *
 *   1. Re-read the cart between every op ............. `convergeCommit` reads
 *      after every applied op AND again immediately before every write.
 *   2. Read back before every `endpoint: 'change'` write, confirming the key
 *      still holds the variant, the plan and EXACTLY `expectedQuantity` units
 *      ............................................... `readBackHolds`, called
 *      against a read taken in the same breath as the write. A mismatch
 *      discards the op and asks again; it never fires.
 *   3. Use `reconcileStatus`, not just `nextOp` ...... the loop switches on
 *      `status`; ONLY 'converged' returns a converged result, and only a
 *      converged result reaches `showSurface('confirmation')`.
 *   3b. State the build's AUTHORITY from what actually happened, and fence the
 *      wire ............................................ `open()` sets
 *      `cartVouchedAtOpen` ONLY where the open read RESOLVED **and** `hydrate()`
 *      produced the build the shopper then edited, `commit()` forwards it, and
 *      `convergeCommit` turns it into `desired.buildAuthority` — defaulting to
 *      ADDITIVE, so a caller who says nothing gets no removals.
 *      The wire fence is in the loop: on an additive build nothing but
 *      `endpoint: 'add'` may be fired, whatever the module returns (F8).
 *   4. Feed every refused opKey back ................. `refused` is a Set that
 *      grows on every failed write THAT LEFT THE CART UNMOVED, and is passed as
 *      `refusedOpKeys` on every subsequent call, so a refusal counts as
 *      OUTSTANDING WORK, not silence. A refusal that DID move the cart is a
 *      PARTIAL APPLY, not a refusal, and is re-read and re-derived instead —
 *      recording it would be a lie about a write that half-landed, and for an
 *      add (whose opKey is the pair, not a line key) it would permanently
 *      suppress the units that did not make it. See the loop's catch block.
 *   5. Pass `desired.kitUnits` = the Kit units hydration reported ... `commit()`
 *      passes `hydratedKitUnits`, captured from `hydrate()` at open — what the
 *      shopper was actually shown when they confirmed.
 *   6. Pass `confirmedBreak` as an OWN property on a FRESH object literal
 *      ............................................... `freshOptions()` and the
 *      `desired` literal are rebuilt inside `convergeCommit` from primitives;
 *      no object from state, from the DOM or from a third-party script is ever
 *      forwarded. Prototype pollution was a proven vector for arming the $47
 *      Kit delete.
 *   7. Bound the loop and bail on an unmoved re-read .. `guardBound()` =
 *      scopedLineCount + desiredSectionCount + 8, and `cartSignature()`
 *      comparison after every applied op.
 *   8. `pendingUnits() === 0` is a progress measure, not a success signal
 *      ............................................... it is reported in the
 *      result for diagnostics only; `buildReadability()` gives the read verdict
 *      BEFORE the loop, without computing an op, and an unreadable build never
 *      touches the cart.
 *   9. On any stall, show the partial-commit surface, never the success screen
 *      ............................................... `commit()` routes every
 *      result through `surfaceForResult`, which returns 'confirmation' ONLY for
 *      an OWN `status === 'converged'`; everything else lands on
 *      `partialCommitConfirm` with local state intact and no retry (AE16).
 *
 * The fresh `_bundle_id` is minted ONCE, in `commit()`, before the loop. Minting
 * it inside would hand every pass a different id and the loop would restamp
 * forever.
 *
 * KTD-H — bundle status is COUNTED, never priced. There is no
 * `eg-bundle-pricing` import, no `/proxy/bundles` fetch and no discount row on
 * this surface; `eg-bundle-nudge.js` returns counts and the footer phrases them
 * as two lines, confirm then nudge.
 *
 * URLs are built from `window.Shopify.routes.root`, which is RELATIVE and so
 * immune to the primary-domain 301 that silently downgrades a POST to a GET
 * (docs/solutions/developer-experience/http-200-is-not-proof-you-got-the-real-
 * page.md). No host is ever hardcoded here.
 *
 * The sibling imports resolve against the theme's flat /assets/ directory on
 * the Shopify CDN (which serves `access-control-allow-origin: *`, required for
 * cross-origin module scripts).
 */

// ───────────────────────────────────────────────────────── the converge loop

/** Slack over the shape of the work: the cart can also be ADDED to, and a
 * third-party watcher can split a line under us. KTD-A widened the old
 * `capturedLines.length + 8` precedent to cover both. */
export const GUARD_SLACK = 8;

/**
 * The loop's bound (KTD-A): `scopedLineCount + desiredSectionCount + 8`.
 * Junk counts degrade to the floor rather than producing an unbounded — or a
 * zero — loop, because either would be a worse failure than a stall.
 */
export function guardBound(scopedLineCount, desiredSectionCount) {
  const lines = Math.max(0, Math.floor(Number(scopedLineCount)) || 0);
  const sections = Math.max(0, Math.floor(Number(desiredSectionCount)) || 0);
  return lines + sections + GUARD_SLACK;
}

/**
 * DID THE CART ACTUALLY MOVE? The one question `nextOp` cannot ask.
 *
 * SORTED, so a pure reshuffle by a cart-watcher reads as UNMOVED and the loop
 * bails rather than spinning. Every field a legitimate op changes is in here —
 * the line key (a property write mints a new one, U0/S1), the quantity, the
 * selling plan and the `_bundle_id` — so no real operation can look like a
 * no-op, and a no-op can never look like progress.
 */
export function cartSignature(items) {
  const list = Array.isArray(items) ? items : [];
  return list
    .map((item) =>
      [
        item?.key ?? '',
        item?.variant_id ?? item?.id ?? '',
        item?.quantity ?? '',
        item?.selling_plan_allocation?.selling_plan?.id ?? '',
        item?.properties?._bundle_id ?? '',
      ].join('|')
    )
    .sort()
    .join('\n');
}

/**
 * Obligation 2 — THE PRECONDITION THAT TRAVELS WITH A WRITE.
 *
 * `nextOp` is pure and cannot read back, so it attaches `expectedQuantity` (the
 * quantity the line held in the read that produced the op) and leaves the
 * verification here. The loss this stops, simulated in U0/S6 and proven live
 * against Bundler on 2026-07-16: a read shows M1(q1) + M2(q1) and produces
 * `restamp M1 quantity:1`; the cart-watcher merges them into M1(q2); the write
 * lands at quantity 1 and the shopper silently loses a unit at HTTP 200. On a
 * `remove-kit` the same window is a $94 delete against a $47 confirmation.
 *
 * An add names no existing line and can only ever leave the shopper with more
 * than they had, so it has nothing to read back.
 */
export function readBackHolds(op, items) {
  if (!op || op.endpoint !== 'change') return true;
  const list = Array.isArray(items) ? items : [];
  const line = list.find((item) => item && item.key === op.lineKey);
  if (!line) return false;
  const variantId = line.variant_id ?? line.id ?? null;
  if (variantId === null || String(variantId) !== String(op.variantId)) return false;
  const rawPlan = line.selling_plan_allocation?.selling_plan?.id ?? null;
  const planId = rawPlan === null || rawPlan === undefined ? null : String(rawPlan);
  if (planId !== (op.sellingPlanId ?? null)) return false;
  return Math.floor(Number(line.quantity)) === op.expectedQuantity;
}

/**
 * THE WIRE SHAPE OF AN ADD IS A STOCK CONTROL, NOT A STYLE CHOICE (finding C).
 *
 * `/cart/add.js` enforces stock DIFFERENTLY depending on the shape of the body
 * you hand it. Proven live on 2026-07-29 against the production catalog, with
 * ten subscribed units of a stock-10 `deny` variant already in the cart:
 *
 *   {"items":[{ id, quantity: 10, … }]}   → HTTP 422, nothing added. CORRECT.
 *   {"id": …, "quantity": 10, … }         → HTTP 200. The cart now holds TWENTY
 *                                           units of a stock-10 variant.
 *
 * The single-object form BYPASSES the cart-total stock check. A shopper could
 * therefore assemble a cart that fails at CHECKOUT — after they have committed,
 * with no way to tell which line is the problem — instead of at add time, where
 * the partial-commit surface would have told them. Every add this file fires
 * goes out in the line-item form, and there is no second add path.
 *
 * The reconcile module stays the authority on the PAYLOAD: `op.body` carries
 * `id`, `quantity`, `selling_plan` and the merged `properties` (the fresh
 * `_bundle_id` plus whatever another app had already written). This function
 * owns only the ENVELOPE, which is a transport concern and belongs with the
 * fetch. All three ride inside the item object untouched — verified live: the
 * 200 echoed back `quantity: 3`, `selling_plan: 4533616913` and BOTH
 * `_bundle_id` and a third-party `Engraving` property.
 *
 * The RESPONSE shape differs too — `{ items: [line] }` rather than the bare
 * line. That is safe here only because `convergeCommit` ignores what `applyOp`
 * returns and re-reads /cart.js instead. Do not start parsing it.
 */
export function cartRequestFor(op) {
  if (op?.endpoint === 'add') return { path: 'cart/add.js', body: { items: [op.body] } };
  return { path: 'cart/change.js', body: op?.body };
}

/** Scoped lines in a cart read — the first half of the guard bound. Uses the
 * SAME scope fence the reconcile module uses (the parsed id map), so the two
 * can never disagree about what counts. The Kit counts: it is a line this
 * surface can, under a confirmed break, be asked to operate on. */
export function countScopedLines(rawMap, items) {
  const map = parseIdMap(rawMap);
  const scoped = new Set();
  for (const cover of map.covers) for (const way of cover.colorways) scoped.add(way.variantId);
  for (const cadence of map.classic.cadences) if (cadence.variantId !== null) scoped.add(cadence.variantId);
  if (map.annual) scoped.add(map.annual.variantId);
  for (const accessory of map.accessories) scoped.add(accessory.variantId);
  if (map.kit) scoped.add(map.kit.variantId);
  return (Array.isArray(items) ? items : []).filter((item) => {
    const id = item?.variant_id ?? item?.id ?? null;
    return id !== null && scoped.has(String(id));
  }).length;
}

/**
 * THE COMMIT CONVERGE LOOP (KTD-A/B/C). Three exits, and only one is success:
 *
 *   read → reconcileStatus → (verify) → apply → re-read → did it move? → repeat
 *
 *   'converged'  the cart matches the build. The ONLY success.
 *   'stalled'    the guard is exhausted, an applied op did not move the cart,
 *                the build could not be read, the cart could not be read, a
 *                write was refused, or the module refused a Kit removal.
 *
 * On any stall THIS LOOP'S OWN ORDERING leaves a superset of the build, never a
 * subset (KTD-C): adds and increases are exhausted before the first decrease,
 * so a bail anywhere leaves extras the shopper can see and delete, never a hole.
 *
 * THE ONE HOLE ORDERING CANNOT PREVENT, stated because it is real and was found
 * live (finding A, production catalog, 2026-07-29): THE CART ITSELF MOVES A LINE
 * UNDER A 422. `/cart/change.js` truncates a write to the maximum quantity it
 * can sell, applies the property write ANYWAY, and re-keys the line — observed
 * directly: a subscribed line asked for 10 came back at 8 carrying the fresh
 * `_bundle_id` under HTTP 422, with a new key. `/cart/add.js` does the same in
 * the other direction ("Only 2 items were added to your cart due to
 * availability" — 422, two units added). So a line can hold FEWER units than the
 * build asks for through no decision of ours.
 *
 * The loop cannot stop that. What it can do — and does — is never mistake it for
 * silence: EVERY refusal is followed by a re-read, and the shortfall is then
 * ordinary outstanding work. The next pass tries to raise the line, is refused
 * for real (a true no-op, the cart does not move), and the run ends on the
 * partial-commit surface. It can never end on 'converged' holding a hole,
 * because 'converged' is `reconcileStatus`'s verdict on a FRESH READ, never on
 * our memory of what we asked for.
 *
 * THE OTHER HOLE ORDERING CANNOT PREVENT, AND THIS ONE WE OWNED (F8, proven
 * live 2026-07-29 against the production catalog): A BUILD THE SHOPPER NEVER
 * AUTHORED AGAINST THEIR REAL CART. `open()` used to swallow a failed
 * `/cart.js` read and open on an empty build, justified in a comment by "commit
 * re-reads anyway". Commit re-reads the CART; it has never re-read the
 * shopper's INTENT. So a cart holding 1 Annual + 2 accessories, committed from
 * a blind open, produced three removes at HTTP 200, emptied the cart and
 * returned 'converged' — the success screen, over a hole.
 *
 * `cartVouchedAtOpen` closes it. It is a POSITIVE observation of the ONE event
 * that makes a missing line an instruction: the open read RESOLVED. It defaults
 * to false, so a caller who does not vouch for the cart gets an ADDITIVE build
 * and no removals at all. See BUILD AUTHORITY in the reconcile module's header
 * for the three states and why 'cleared' is still honoured.
 *
 * `bundleId` is minted by the CALLER, once, before this is entered — see the
 * header. `readCart` returns a /cart.js items array; `applyOp` fires one
 * operation and THROWS if the cart refused it.
 */
export async function convergeCommit({
  rawMap,
  desiredLines,
  bundleId,
  readCart,
  applyOp,
  confirmedBreak = false,
  cartVouchedAtOpen = false,
  kitUnits = 0,
  scopedLineCount = 0,
  desiredSectionCount = 0,
} = {}) {
  const broken = confirmedBreak === true;
  // F8 — the whole verdict, in one place, from one event. `=== true` and not a
  // truthiness test: this is the argument that unlocks deletion, and a caller
  // handing us a Response, a length or a string has not answered the question.
  const authority = cartVouchedAtOpen === true ? AUTHORITATIVE : ADDITIVE;

  // Obligations 5 and 6 — a FRESH OBJECT LITERAL with OWN properties. Nothing
  // received from state, the DOM or a third-party script is forwarded, because
  // an inherited `kitBroken` is a $47 delete nobody asked for.
  const accountedKitUnits = Math.floor(Number(kitUnits));
  const desired = {
    lines: Array.isArray(desiredLines) ? desiredLines : [],
    kitBroken: broken,
    kitUnits: Number.isFinite(accountedKitUnits) && accountedKitUnits > 0 ? accountedKitUnits : 0,
    buildAuthority: authority,
  };
  const freshOptions = () => ({ confirmedBreak: broken, refusedOpKeys: [...refused] });

  const bound = guardBound(scopedLineCount, desiredSectionCount);
  const refused = new Set();
  const applied = [];
  /** Ops the cart answered with a non-2xx AND still moved the cart for. Kept
   * apart from both other lists because it is neither: reporting one as applied
   * would claim the write landed as asked, and reporting it as refused would
   * claim nothing happened. Diagnostics only — nothing branches on it. */
  const partiallyApplied = [];
  let passes = 0;

  const done = (status, reason, items) => ({
    status,
    reason,
    authority,
    appliedOpKeys: [...applied],
    refusedOpKeys: [...refused],
    partiallyAppliedOpKeys: [...partiallyApplied],
    passes,
    guardBound: bound,
    // Obligation 8 — a PROGRESS measure, reported for diagnostics only. It is
    // 0 both when everything landed and when the module refused to read the
    // build at all, so it never decides anything here.
    pendingUnits: items === null ? null : pendingUnits(rawMap, desired, items, bundleId, freshOptions()),
  });

  // Obligation 8 — the read verdict WITHOUT computing an op. Every removal in
  // the module is driven by a pair MISSING from the desired build, so a build
  // this module cannot parse must never reach the loop: at one level down,
  // "unreadable" is indistinguishable from "the shopper asked for zero of it".
  const readability = buildReadability(rawMap, desired);
  if (!readability.readable) return done('stalled', readability.reason, null);

  let items;
  try {
    items = await readCart();
  } catch (error) {
    return done('stalled', 'cart-unreadable', null);
  }
  let signature = cartSignature(items);
  let guard = bound;

  for (;;) {
    passes += 1;

    // Obligation 3 — `null` from `nextOp` means "done" OR "refused to act";
    // only `status` distinguishes them, and only 'converged' is success.
    const verdict = reconcileStatus(rawMap, desired, items, bundleId, freshOptions());
    if (verdict.status === 'converged') return done('converged', null, items);
    if (verdict.status === 'stalled') return done('stalled', verdict.reason, items);

    const op = verdict.op;

    // Obligation 3b — THE WIRE FENCE (F8). The reconcile module already refuses
    // to emit anything but an `add` on an additive build; this is the same rule
    // stated again at the ONE line where a write actually leaves this file, so
    // that a regression, a refactor or a third `desired` shape in there can
    // never reach a real cart. Defence in depth is cheap here: one comparison
    // against a value derived from a single boolean argument.
    //
    // Reaching this means the module and this fence disagree, which is a bug in
    // one of them — so it ends the run rather than skipping the op, and it ends
    // it with adds already exhausted (bucket 1 runs first), leaving a superset.
    if (authority !== AUTHORITATIVE && op.endpoint !== 'add') {
      return done('stalled', 'build-not-authoritative', items);
    }

    // Obligation 7 — termination is ours. Every attempt costs a tick, whether
    // or not it reaches the network.
    if (guard <= 0) return done('stalled', 'guard-exhausted', items);
    guard -= 1;

    // Obligations 1 and 2 — re-read IMMEDIATELY before the write, and verify
    // the op against THAT read. A mismatch means the line moved in the window;
    // discard the op and ask again rather than writing a stale quantity.
    let fresh;
    try {
      fresh = await readCart();
    } catch (error) {
      return done('stalled', 'cart-unreadable', items);
    }
    items = fresh;
    signature = cartSignature(fresh);
    if (!readBackHolds(op, fresh)) continue;

    try {
      await applyOp(op);
    } catch (error) {
      // A NON-2xx IS NOT PROOF OF A NO-OP. Both findings that reached this
      // branch were proven live on 2026-07-29 against the production catalog:
      //
      //   change.js  422 "Only 8 items were added to your cart due to
      //              availability" — the line was set to 8, the fresh
      //              `_bundle_id` WAS written, and the key changed (A).
      //   add.js     422 "Only 2 items were added to your cart due to
      //              availability" — two units were added (D).
      //
      // So the only honest move is to LOOK. Re-read first, decide afterwards,
      // and let the next `nextOp` work from what the cart now holds rather than
      // from what we asked it to hold.
      let afterRefusal;
      try {
        afterRefusal = await readCart();
      } catch (readError) {
        return done('stalled', 'cart-unreadable', items);
      }
      const movedUnderRefusal = cartSignature(afterRefusal) !== signature;
      items = afterRefusal;
      signature = cartSignature(afterRefusal);

      if (movedUnderRefusal) {
        // A PARTIAL APPLY, NOT A REFUSAL — so do not record one.
        //
        // First because it is simply false, and this list is what the
        // partial-commit surface reports and what `console.warn`s: "the cart
        // refused this" is the wrong sentence about a write that half-landed.
        //
        // Second because a refused key makes `nextOp` return NULL — it does not
        // skip to the next op, it stops the whole run (`all-remaining-ops-
        // refused`). Marking a half-landed write therefore risks ending the run
        // on work a retry could still finish. Whether the mark can bite depends
        // on whether the key survives the partial write, and BOTH answers exist:
        // a change op is keyed on the LINE key, which a property write re-mints
        // (so the mark usually goes stale harmlessly), but an add op is keyed on
        // the (variantId, sellingPlanId) PAIR, and a quantity-only change does
        // NOT re-key at all — both confirmed live on 2026-07-29. A key that
        // survives plus a stale mark is a premature stall.
        //
        // So: re-read, believe the cart, and let the next `nextOp` decide from
        // what is actually there. This cannot spin — every pass still costs a
        // guard tick (obligation 7), and the moment the cart stops moving the
        // branch below fires and the refusal sticks.
        partiallyApplied.push(op.opKey);
        continue;
      }

      // Obligation 4 — a TRUE refusal: non-2xx and the cart did not move. NOW
      // record it, so it is not re-offered AND so it counts as outstanding
      // work: a refused add that reads as silence is what once cleared the way
      // for the Kit deletion meant to follow it.
      refused.add(op.opKey);
      continue;
    }

    applied.push(op.opKey);

    let after;
    try {
      after = await readCart();
    } catch (error) {
      return done('stalled', 'cart-unreadable', items);
    }
    // KTD-A — an applied op that did not move the cart is the one shape a
    // status code cannot show you. Bail; never re-offer it.
    if (cartSignature(after) === signature) return done('stalled', 'cart-did-not-move', after);
    items = after;
    signature = cartSignature(after);
  }
}

// ─────────────────────────────────────────────────────────────── DOM wiring

const SELECTORS = {
  root: '[data-eg-configurator]',
  dialog: '[data-eg-configurator-dialog]',
  shell: '[data-eg-configurator-shell]',
  close: '[data-eg-configurator-close]',
  stage: '[data-eg-stage]',
  hero: '[data-eg-hero]',
  thumbs: '[data-eg-thumbs]',
  // Design pass 2026-07-29, batch 2: points at the new `.eg-configurator
  // __scroll` wrapper, not `[data-eg-sections]` directly — that wrapper is
  // now the element with the real, bounded, overflowing box (it holds the
  // header too), so it's the one whose scrollHeight/scrollTop/clientHeight
  // mean anything (see syncFooterEdge below). Every existing `el.sections`
  // use (scroll-spy bounds, subscribe-row/step-kind lookups, the scroll/
  // focusin listeners) still works unchanged: the wrapper is a strict
  // ancestor of everything `[data-eg-sections]` used to be queried for.
  sections: '[data-eg-scroll]',
  accessoriesGroup: '[data-eg-section-group="accessories"]',
  accessories: '[data-eg-accessories]',
  breakdownLines: '[data-eg-breakdown-lines]',
  breakdownGroup: '[data-eg-section-group="breakdown"]',
  readOnly: '[data-eg-readonly]',
  readOnlyLines: '[data-eg-readonly-lines]',
  readOnlyNote: '[data-eg-readonly-note]',
  // Design pass (2026-07-29, batch 3) — the OUTER section-group wrapper,
  // distinct from `inclusions` below (the inner content box). Hiding this
  // too, not just the inner one, is what stops it contributing its own
  // stray 80px of empty padding-top between Annual and Accessories when
  // there is nothing to show — see component-eg-configurator.css's
  // `.eg-configurator__section-group + .eg-configurator__section-group`.
  inclusionsGroup: '[data-eg-section-group="inclusions"]',
  inclusions: '[data-eg-inclusions]',
  inclusionsHeading: '[data-eg-inclusions-heading]',
  inclusionsText: '[data-eg-inclusions-text]',
  // Was `inclusionsHighlight`, a button that scrolled to the subscribe
  // checkbox (Kyle retired the control 2026-07-30 — see the liquid). Same
  // trial-only show/hide, but the element is now a plain note.
  inclusionsSubnote: '[data-eg-inclusions-subnote]',
  footer: '[data-eg-footer]',
  // U3 (2026-10-06) — the ONE live cover-waitlist widget. The Liquid renders
  // it once inside the Cover section-group, hidden and `data-eg-keep`; the
  // renderer only ever reparents it (see `renderSections`).
  coverWaitlist: '[data-eg-cover-waitlist]',
  error: '[data-eg-configurator-error]',
  bundleConfirm: '[data-eg-bundle-confirm]',
  nudge: '[data-eg-nudge]',
  total: '[data-eg-total]',
  submit: '[data-eg-configurator-submit]',
  confirm: '[data-eg-confirm]',
  confirmMessage: '[data-eg-confirm-message]',
  confirmConsequences: '[data-eg-confirm-consequences]',
  confirmCartLink: '[data-eg-confirm-cart-link]',
  confirmPrimary: '[data-eg-confirm-primary]',
  confirmSecondary: '[data-eg-confirm-secondary]',
  viewBuild: '[data-eg-configurator-view="build"]',
  viewConfirmation: '[data-eg-configurator-view="confirmation"]',
  confirmationHeading: '[data-eg-confirmation-heading]',
  confirmationText: '[data-eg-confirmation-text]',
  confirmationSecondary: '[data-eg-confirmation-secondary]',
  announce: '[data-eg-configurator-announce]',
};

/** Sitewide open CTAs and the URL-arrival marker live OUTSIDE the shell. */
const OPEN_TRIGGER = '[data-eg-configurator-open]';
const AUTOOPEN_MARKER = '[data-eg-configurator-autoopen]';

/** State kind → the shell's section-group name (R7 order is the shell's). */
const GROUP_FOR_KIND = { cover: 'cover', classic: 'classics', annual: 'annual' };
/** How a section names itself in its legend: "Your second cover" (R8). */
const NOUN_FOR_KIND = { cover: 'cover', classic: 'Classic', annual: 'Annual' };
const ORDINALS = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth'];

/** Sections are uncapped (R8), so the words run out long before the sections do. */
function ordinalWord(index) {
  return ORDINALS[index] ?? `#${index + 1}`;
}

/** Placeholder art: dev products carry no images, and a missing image must not break the stage. */
function imageNode(url, label, className) {
  if (typeof url === 'string' && url !== '') {
    const img = document.createElement('img');
    img.className = className;
    img.src = url;
    img.alt = '';
    img.loading = 'lazy';
    return img;
  }
  const placeholder = document.createElement('span');
  placeholder.className = `${className} eg-configurator__placeholder`;
  placeholder.textContent = (label || '').slice(0, 1).toUpperCase();
  placeholder.setAttribute('aria-hidden', 'true');
  return placeholder;
}

/** R9/R27 — the label names the REASON, because "sold out" and "your other
 * sections already claimed them all" are different facts to a shopper. */
function unavailableLabel(option) {
  if (option.reason === 'claimed') return `only ${option.limit} available`;
  if (option.reason === 'sold-out') return 'sold out';
  return 'unavailable';
}

/** R9 — one Cover step is ONE selection spanning EVERY cover product in the id
 * map, and the shopper needs to see which product a colorway belongs to.
 *
 * THE GROUPING IS DERIVED, NEVER NAMED. Production ships two products today
 * ("the Heirloom Cover", "the Minimalist Sleeve"), and neither string — nor the
 * number two — appears anywhere in this file or its CSS. A third cover product
 * added to the id map groups on its own, with no code change here.
 *
 * Order is the id map's product order: `coverOptionsFor` emits products outer
 * and colorways inner, and a Map keyed on first appearance preserves exactly
 * that. Keying rather than run-chunking also means a product whose colorways
 * somehow arrive non-adjacent gets ONE heading, not two.
 *
 * A product with no colorways contributes no options (parseIdMap drops it
 * outright), so this can never emit a heading over an empty box — the groups
 * are built from what is pickable, not from the roster.
 */
export function groupCoverOptions(options) {
  const byKey = new Map();
  for (const option of Array.isArray(options) ? options : []) {
    if (!option || typeof option !== 'object') continue;
    const title = typeof option.coverTitle === 'string' ? option.coverTitle : '';
    const key = typeof option.coverKey === 'string' && option.coverKey !== '' ? option.coverKey : title;
    let group = byKey.get(key);
    if (!group) {
      group = { key, title, options: [] };
      byKey.set(key, group);
    }
    group.options.push(option);
  }
  return [...byKey.values()];
}

/**
 * D4 (2026-10-06) — IS THIS COVER PRODUCT BETWEEN RUNS? A group every one of
 * whose colorways is unbuyable has no grid worth drawing: a wall of greyed
 * cards is nine different ways of saying no. `renderCoverSection` puts the
 * waitlist capture there instead.
 *
 * THE TWO REASONS THAT COUNT ARE THE TWO THAT ARE ABOUT THE CATALOG.
 * `sold-out` (the map says the variant has no stock) and `unavailable` (the
 * variant is not in the map at all) are facts about the product. `claimed` is
 * not: it means THIS shopper's other sections already took every one of a
 * limited colorway, which the shopper fixes by changing her own build — there
 * is nothing to wait for, so a `claimed` group keeps its cards.
 *
 * Same rule as `groupCoverOptions` above: DERIVED, NEVER NAMED. No product
 * name and no count appears here. The colorways coming back in stock takes the
 * widget away again with no deploy, which is the whole point of reading it off
 * the data.
 *
 * REFLECTION STILL WINS (R27, 2026-10-06). A build hydrated from the cart can
 * hold a colorway whose product has since sold out entirely. Her card is in
 * her build, so it stays on the rail (ticked, operable, as R27 already
 * guarantees for an over-limit selection) and the group keeps its grid; the
 * waitlist only replaces a grid that has nothing of hers in it.
 */
const WAITLIST_REASONS = new Set(['sold-out', 'unavailable']);

export function coverGroupWaitlisted(group) {
  const options = Array.isArray(group) ? group : Array.isArray(group?.options) ? group.options : [];
  // An empty group is not a waitlist — it is nothing. (`groupCoverOptions`
  // cannot emit one, but the predicate is exported and must not claim a
  // product is between runs on the strength of no evidence at all.)
  if (options.length === 0) return false;
  if (options.some((option) => option && option.selected === true)) return false;
  return options.every(
    (option) => option && option.selectable !== true && WAITLIST_REASONS.has(option.reason)
  );
}

/**
 * R6 — "with nothing selected in a section, shared photos only". The map
 * appends the product's shared photos to EVERY variant's gallery, so the shared
 * set is exactly the images common to all of that product's variants. Derived
 * rather than authored, so a new colorway needs no theme change.
 *
 * ONE PRODUCT'S VARIANTS. Handing this a list that spans several products asks
 * a different question and gets a misleading answer — see below.
 */
export function sharedImages(entries) {
  const list = Array.isArray(entries) ? entries : [];
  const lists = list.map((entry) => entry?.images).filter((each) => Array.isArray(each) && each.length > 0);
  if (lists.length === 0) return [];
  return lists[0].filter((src) => lists.every((each) => each.includes(src)));
}

/**
 * D6 — "SHARED" IS PER PRODUCT, NOT ACROSS PRODUCTS.
 *
 * `sharedImages` intersects, which is right for the variants of ONE product and
 * wrong for a list spanning several. The cover kind was being handed every
 * colorway of every cover product flattened together, so the moment production
 * shipped a SECOND cover product the intersection went empty and R6's "shared
 * photos" silently degraded to NO photo: a shopper opening the modal with
 * nothing selected met a bare placeholder letter across the whole left
 * two-thirds where the hero belongs. It degrades exactly when the catalogue
 * grows, which is the direction catalogues go.
 *
 * Intersecting INSIDE each product and concatenating keeps R6's rule intact and
 * leaves the stage something to show as long as any one product has a shared
 * photo. Order is the id map's; deduped, so a photo two products happen to
 * share appears once.
 */
export function sharedAcrossProducts(groups) {
  return [...new Set((Array.isArray(groups) ? groups : []).flatMap((group) => sharedImages(group)))];
}

function joinList(parts) {
  if (parts.length <= 1) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** R22 — the missing legs, phrased. Never a figure, on either line. */
const LEG_PHRASE = { 'a cover': 'one more cover', booklets: 'more booklets', 'the Annual': 'one more Annual' };

/**
 * Review 2026-07-29 (#10) — ONE source for the nudge sentence. The visible
 * line and the screen-reader announcement used to phrase the bundle
 * independently and drifted: the announcement said "unlock another" with zero
 * bundles complete and skipped LEG_PHRASE — the same two-sources-for-one-
 * surface class D5 already named. Both surfaces now read this. Empty string
 * means "no nudge"; the sentence always ends with a full stop.
 */
export function nudgeSentence(status) {
  if (!status || !Array.isArray(status.missing) || status.missing.length === 0) return '';
  const legs = status.missing.map((leg) => (status.complete > 0 ? LEG_PHRASE[leg] ?? leg : leg));
  return status.complete > 0
    ? `Add ${joinList(legs)} to unlock another.`
    : `Add ${joinList(legs)} to unlock a bundle discount.`;
}

// ═══════════════════════════════════════════════════════════════════════════
// U9 — THE FOUR REPORT/CONFIRM SURFACES (R15, R19, R19a, R19b, R23, R26)
//
// Everything in this block is PURE and DOM-free, so the branch logic behind
// each surface can be driven in the node test environment
// (tests/eg-configurator-surfaces.test.js). The DOM half — which slot is
// populated, where focus lands, what is announced — lives inside
// `initConfigurator` and is walked in U12.
//
// ⚠ THE PLAN IS WRONG ABOUT THE COMMUNITY VERDICT, AND THIS IS THE CODE THAT
// HAS TO BE RIGHT ABOUT IT.
//
// U9's plan text says the Kit-break confirm decides whether the community
// survives by falling back to "the spend threshold on what remains — the
// Classic alone is $29, the Annual alone is $30, both short of $47." THERE IS
// NO THRESHOLD. OQ1 (2026-07-28) replaced spend with composition, and
// CONCEPTS.md's Kit entry says so: an order qualifies on (a) the Kit product,
// (b) any subscription line, or (c) at least one Annual AND at least one
// Classic, at any price. Several of the plan's example ANSWERS survive the
// correction; the reasoning does not, and at least one case flips — a large
// pile of booklets with no Annual used to clear $47 and now qualifies for
// nothing.
//
// So: the verdict is READ from `inclusionsFor()` (U3) and computed nowhere
// else. `projectKitBreak` deliberately projects LINES, not a total, and the
// total it does compute is only ever spent on consequence (a), "what it
// costs". Two sources for one number is how the old bundle-pricing display
// drifted (U9's own execution note).
//
// The Renew course is NOT a component of the Kit (R18b, CONCEPTS.md), so no
// consequence here says the shopper loses a course. There is no course to
// lose.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * R15 — the two discard registers, keyed on whether the cart was empty AT
 * OPEN, never at close. The distinction is real: with an empty-at-open cart
 * nothing exists to update, so "Close without updating your cart" would name a
 * thing that does not exist. A shopper who opened empty and then built a full
 * trio locally still gets the first register, because nothing reached the cart.
 */
export function discardCopy(cartEmptyAtOpen) {
  return cartEmptyAtOpen === true
    ? {
        register: 'empty-at-open',
        message: 'Leave without saving your build?',
        confirmLabel: 'Leave',
        cancelLabel: 'Keep building',
      }
    : {
        register: 'cart-at-open',
        message: 'Close without updating your cart.',
        confirmLabel: 'Discard changes',
        cancelLabel: 'Keep building',
      };
}

/**
 * AE16 — every stall reason `convergeCommit` and `reconcileStatus` can hand
 * back, in a sentence a shopper can act on. A raw slug in the UI would be this
 * surface telling somebody whose cart is genuinely wrong to go and read our
 * source code, so nothing here leaks one.
 *
 * The Kit reasons are deliberately reassuring on the one point that matters to
 * a shopper looking at a $47 line: it is still there.
 *
 * `build-not-authoritative` (F8) is written to sit correctly in FRONT OF BOTH
 * of `partialCommitCopy`'s second sentences, because this is the one stall that
 * routinely lands with adds already applied: "…so we didn't take anything out
 * of it. Some of your changes are already in your cart" and "…so we didn't take
 * anything out of it. Nothing reached your cart." both read true. It says what
 * happened (we could not check; we removed nothing) and never why in our terms
 * — no slug, no "blind open", no mention of a failed read the shopper cannot
 * act on.
 */
export const STALL_SENTENCES = {
  'unreadable-build': 'We couldn’t read your build, so we stopped before changing anything.',
  'unreadable-lines': 'We couldn’t read your build, so we stopped before changing anything.',
  'cart-unreadable': 'We lost contact with your cart partway through the update.',
  'build-not-authoritative':
    'We couldn’t check what was already in your cart when you opened this, so we didn’t take anything out of it.',
  'guard-exhausted': 'Your cart kept moving while we were updating it, so we stopped.',
  'cart-did-not-move': 'One of the updates didn’t take, so we stopped rather than sending it again.',
  'work-outstanding': 'Your cart turned down part of the update, so we stopped before removing anything.',
  'all-remaining-ops-refused': 'Your cart turned down the last of the changes.',
  'kit-contents-incomplete': 'We couldn’t confirm what your Getting Started Kit holds, so we left it in your cart.',
  'kit-piece-not-in-cart': 'The pieces you kept aren’t all in your cart yet, so we left your Getting Started Kit alone.',
  'kit-units-unaccounted': 'Your cart holds more Getting Started Kits than this build covers, so we left them all alone.',
  'kit-removal-unavailable': 'We couldn’t take your Getting Started Kit out, so it is still in your cart.',
};

/** An unrecognised reason is still a shopper whose cart may be wrong. */
const STALL_FALLBACK = 'We couldn’t finish updating your cart.';

/**
 * AE16 — the partial-commit report. Honest about WHICH failure it was and
 * honest about whether anything landed: the pre-loop bails write nothing at
 * all, and telling that shopper "some of your changes are already in your
 * cart" would be a second, avoidable lie on top of the first failure.
 *
 * There is deliberately no retry copy on any path. A retry re-runs a loop whose
 * adds already landed, which compounds the duplication rather than fixing it.
 */
export function partialCommitCopy(reason, appliedCount = 0) {
  const sentence = STALL_SENTENCES[reason] ?? STALL_FALLBACK;
  const applied = Math.floor(Number(appliedCount));
  const landed =
    Number.isFinite(applied) && applied > 0
      ? 'Some of your changes are already in your cart, so please check it before you check out.'
      : 'Nothing reached your cart.';
  return { reason, sentence, landed, message: `${sentence} ${landed}` };
}

/**
 * Obligation 9 / AE16 — the whole routing rule, in one testable place: ONLY an
 * OWN `status === 'converged'` is success. Absent is not converged, inherited
 * is not converged, and a stall never falls through to the success view.
 */
export function surfaceForResult(result) {
  if (!result || typeof result !== 'object') return 'partial-commit';
  if (!Object.prototype.hasOwnProperty.call(result, 'status')) return 'partial-commit';
  return result.status === 'converged' ? 'confirmation' : 'partial-commit';
}

/** The page's own name, for R23's "Return to {page}". Shopify's default title
 * is `{page} – {shop}`; anything we cannot split is used whole. */
export function pageNameFromTitle(documentTitle) {
  const raw = typeof documentTitle === 'string' ? documentTitle : '';
  return raw.split(/\s+[–—|·]\s+/)[0].trim();
}

/**
 * R23 / AE8 — the secondary action's arrival split. A URL arrival has no page
 * behind the modal worth returning to (R2), so it is offered the homepage; a
 * CTA arrival is offered the page it came from, by name, and closing the modal
 * IS the return.
 */
export function confirmationSecondary({ arrivedByUrl, pageName, homeHref, returnHref, homeLabel } = {}) {
  const home = typeof homeHref === 'string' && homeHref !== '' ? homeHref : '/';
  // `homeLabel` exists for the staged re-open (2026-07-31): while the site
  // root was itself gated, "home" was Booth, and a button that still said
  // "Go to the homepage" would have been lying about where it goes. Absent or
  // blank — which is what the theme passes once home is the root again, i.e.
  // since 2026-08-13, and with the gate down — keeps the normal words. So this
  // is inert unless a window diverts home a second time.
  const label = typeof homeLabel === 'string' && homeLabel.trim() !== '' ? homeLabel.trim() : 'Go to the homepage';
  if (arrivedByUrl === true) return { label, href: home, closesModal: false };
  const name = typeof pageName === 'string' && pageName.trim() !== '' ? pageName.trim() : 'the page you were on';
  const href = typeof returnHref === 'string' && returnHref !== '' ? returnHref : home;
  return { label: `Return to ${name}`, href, closesModal: true };
}

/**
 * D3 — WHAT A CLOSE REQUEST MEANS, PER SURFACE. Pulled out of the event
 * handlers so the routing can be pinned in the node environment, and so the
 * keydown path and the `cancel` path can never answer it differently.
 *
 *   'decline-overlay'      an overlay is up: Escape declines THAT (keeps the
 *                          Kit, keeps building, goes back to the build) rather
 *                          than closing the dialog out from under a question
 *                          the shopper has not answered.
 *   'exit-confirmation'    the commit already landed; there is nothing to lose.
 *   'prompt-or-close'      the build view: run the R15 discard prompt if the
 *                          build is dirty, close outright if it is not.
 */
export function closeRequestAction(surface, { committing = false } = {}) {
  // Review 2026-07-29 (#1) — a commit's cart writes cannot be aborted once
  // fired, so while they are landing the only honest answer to ANY close
  // request is "not yet". Checked before the surface, because the surface
  // during a commit is still 'build'. Own strict boolean, same discipline as
  // every other flag that gates a cart-affecting decision.
  if (committing === true) return 'refuse-committing';
  if (surface === 'confirm' || surface === 'partial-commit') return 'decline-overlay';
  if (surface === 'confirmation') return 'exit-confirmation';
  return 'prompt-or-close';
}

/**
 * D3 — ESCAPE IS TAKEN ON `keydown`, WHERE IT CAN STILL BE STOPPED.
 *
 * The `cancel` event was the wrong place, and it failed exactly where it cost
 * the most. Per HTML's close-watcher model a `cancel` is only `cancelable`
 * while the page holds history-action activation; a SECOND close request in one
 * modal session has none left, so `cancel` arrives with `cancelable === false`,
 * `preventDefault()` is a silent no-op, and the dialog closes regardless.
 * Measured on this surface 2026-07-29: with the discard prompt up, a second
 * Escape force-closed the modal and the whole local build went with it. The
 * Kit-break confirm shares the slot and shared the bug — there the lost answer
 * is a $47 line.
 *
 * The close-watcher spec routes an Escape through a keydown FIRST and abandons
 * the close request if that keydown is canceled, so cancelling here means no
 * `cancel` event is ever generated and its cancelability stops mattering. This
 * is the spec-blessed path, not a Chrome workaround.
 *
 * Returns whether this keydown IS a close request this file has taken over.
 */
export function takeEscapeKeydown(event, { open } = {}) {
  if (open !== true) return false;
  if (!event || event.key !== 'Escape' || event.defaultPrevented === true) return false;
  event.preventDefault();
  return true;
}

/**
 * D3's BACKSTOP. If a close request ever reaches `close` anyway — a UA that
 * ignores the cancelled keydown, a non-cancelable `cancel` from some other
 * source — the shopper is one event away from losing work they can still see.
 * So an UNASKED-FOR close is undone while there is anything to lose: an overlay
 * is up (a question is outstanding) or the build differs from what it opened as.
 *
 * `deliberate` is the only thing that lets a close through, and this file sets
 * it around every `dialog.close()` it makes itself.
 */
export function shouldResurrect({ deliberate, surface, dirty, committing } = {}) {
  if (deliberate === true) return false;
  // Review 2026-07-29 (#1) — a close nobody asked for while a commit's writes
  // are still landing must come straight back: the loop's outcome (success or
  // the partial-commit warning) would otherwise paint into a closed dialog
  // where nobody can see it. `isDirty()` cannot carry this — commit does not
  // change the local build, so the build reads clean mid-commit.
  if (committing === true) return true;
  if (surface === 'confirm' || surface === 'partial-commit') return true;
  return dirty === true;
}

/**
 * D4 — THE WRAP STOP THAT ESCAPES THE FOCUS TRAP.
 *
 * `<dialog>.showModal()` keeps focus inside the dialog's subtree, but Chrome
 * still spends one stop per cycle on `<body>` at the wrap point. Measured with
 * the discard confirm up: `Leave → Keep building → BODY → DIALOG → Leave`, so
 * `document.activeElement === document.body` at half the stops and the focus
 * ring vanished mid-decision on a cart-affecting question. (The `DIALOG` stop
 * was separate and is gone with D2: a scrollable dialog is a keyboard-focusable
 * scroller in Chrome.) It is not overlay-specific — the build view wraps
 * through `<body>` too, from the commit button back round to the close button.
 * R26 names this exactly: focus on `<body>` "escapes the dialog's focus trap".
 *
 * Given the live surface's tab stops in document order and where focus is among
 * them, this names the stop the wrap must land on instead — or `null` when the
 * browser's own next stop is a real control and nothing needs doing.
 *
 * `activeIndex === -1` is focus that is ALREADY outside the live surface (the
 * stray `<body>` stop, or a control on a surface that has since been replaced),
 * and it is pulled back in rather than left to wander.
 */
export function tabWrapTarget({ shiftKey = false, activeIndex = -1, count = 0 } = {}) {
  if (!Number.isInteger(count) || count <= 0) return null;
  if (activeIndex < 0 || activeIndex >= count) return shiftKey ? 'last' : 'first';
  if (shiftKey === true) return activeIndex === 0 ? 'last' : null;
  return activeIndex === count - 1 ? 'first' : null;
}

/** R19 — the confirm's wording, verbatim from the requirement. */
export const KIT_BREAK_MESSAGE =
  'You’ve already got a Getting Started Kit in your cart. You’re about to change it. Are you sure you want to proceed?';

/**
 * THE KIT'S PIECES, AS THEY WOULD STAND ONCE THE PENDING EDIT LANDS (R19a).
 *
 * State raises `pendingBreak` INSTEAD of mutating, so at the moment the confirm
 * is shown nothing has changed yet — which is exactly what makes the confirm
 * honest and exactly what makes this projection necessary. It replays the one
 * pending change over the `fromKit` sections and emits what survives.
 *
 * Every price is READ OFF THE STATE VIEW the renderers already draw from (the
 * option's own `price`/`memberPrice`/`oneOffPrice`, the Annual section's
 * `price`), so this is not a second pricing rule — it is the same numbers,
 * asked a different question. The one thing it does NOT do is compute a
 * verdict: `pieces` exists so `inclusionsFor()` can be asked (R13a).
 */
export function projectKitBreak(state) {
  const source = state !== null && typeof state === 'object' ? state : {};
  const pending = source.pendingBreak !== null && typeof source.pendingBreak === 'object' ? source.pendingBreak : null;
  const change = pending && pending.change !== null && typeof pending.change === 'object' ? pending.change : null;
  const sections = Array.isArray(source.sections) ? source.sections : [];
  const accessories = Array.isArray(source.accessories) ? source.accessories : [];
  const kitPrice = Number(source.kitLine?.linePrice) || 0;

  /** The pending change, but only where it actually points. */
  const editOf = (section) => (change && pending.sectionId === section.id ? change : null);

  const pieces = [];
  for (const section of sections) {
    if (!section || typeof section !== 'object' || section.fromKit !== true) continue;
    const edit = editOf(section);
    // A removed section keeps nothing.
    if (edit && edit.type === 'remove') continue;
    const options = Array.isArray(section.options) ? section.options : [];

    if (section.kind === 'cover') {
      const variantId = edit && edit.type === 'cover' ? edit.variantId : section.coverVariantId;
      const option = options.find((item) => item && item.variantId === variantId) ?? null;
      if (option) {
        pieces.push({
          kind: 'cover',
          label: `${option.coverTitle ?? ''} ${option.title ?? ''}`.trim(),
          quantity: 1,
          price: Number(option.price) || 0,
          subscription: false,
        });
      }
    } else if (section.kind === 'classic') {
      const subscribe = edit && edit.type === 'subscribe' ? edit.value === true : section.subscribe === true;
      const booklets = edit && edit.type === 'cadence' ? edit.booklets : section.cadence;
      const option =
        booklets === null || booklets === undefined
          ? null
          : options.find((item) => item && item.booklets === booklets) ?? null;
      if (option) {
        // R10's fork, read off the option view: same variant either way, the
        // plan and the price move with the checkbox.
        const price = Number(subscribe ? option.memberPrice : option.oneOffPrice) || 0;
        pieces.push({ kind: 'classic', label: option.label ?? '', quantity: 1, price, subscription: subscribe });
      }
    } else if (section.kind === 'annual') {
      const present = edit && edit.type === 'annual' ? edit.value === true : section.present === true;
      if (present) {
        pieces.push({
          kind: 'annual',
          label: section.title || 'the Annual',
          quantity: 1,
          price: Number(section.price) || 0,
          subscription: false,
        });
      }
    }
  }

  for (const accessory of accessories) {
    if (!accessory || typeof accessory !== 'object' || accessory.fromKit !== true) continue;
    const edited = change && change.type === 'accessory' && pending.variantId === accessory.variantId;
    const raw = Math.floor(Number(edited ? change.quantity : accessory.quantity));
    const quantity = Number.isFinite(raw) && raw > 0 ? raw : 0;
    if (quantity === 0) continue;
    pieces.push({
      kind: 'accessory',
      label: accessory.title ?? '',
      quantity,
      price: Number(accessory.price) || 0,
      subscription: false,
    });
  }

  const partsTotal = pieces.reduce((sum, piece) => sum + piece.price * piece.quantity, 0);
  return { pieces, partsTotal, kitPrice, delta: partsTotal - kitPrice };
}

/**
 * R19a — the two live consequences, in order: what it costs, then whether the
 * six weeks of community survive.
 *
 * (a) is arithmetic over the KEPT pieces against the Kit's own line price —
 *     both read from the state view, neither authored. The plan's "$59 of parts
 *     against $47 makes a typical change about $12" is an EXAMPLE, not a
 *     constant, and does not appear anywhere below.
 * (b) is `inclusions`, handed in by the caller having asked `inclusionsFor()`
 *     about the projected build. No threshold, no total, no second rule — the
 *     verdict arrives already decided and this function only phrases it.
 */
export function kitBreakConsequences(projection, inclusions, money) {
  const { pieces, partsTotal, kitPrice, delta } = projection;
  const out = [];

  if (pieces.length === 0) {
    out.push(`You’d keep none of the Getting Started Kit’s pieces, so the ${money(kitPrice)} Getting Started Kit simply comes out of your cart.`);
  } else if (delta > 0) {
    out.push(
      `The pieces you keep come to ${money(partsTotal)} on their own, against the Getting Started Kit’s ${money(kitPrice)} — about ${money(delta)} more.`
    );
  } else if (delta < 0) {
    out.push(
      `The pieces you keep come to ${money(partsTotal)} on their own, against the Getting Started Kit’s ${money(kitPrice)} — ${money(-delta)} less.`
    );
  } else {
    out.push(`The pieces you keep come to ${money(partsTotal)} on their own — the same as the Getting Started Kit’s ${money(kitPrice)}.`);
  }

  if (inclusions.trial === true && inclusions.subscribed === true) {
    out.push('Your community access stays — a subscription keeps it going for as long as you subscribe.');
  } else if (inclusions.trial === true) {
    out.push('Your six weeks in the community stay — your build still holds an Annual and a Classic.');
  } else {
    out.push('Your six weeks in the community would go — your build would no longer hold both an Annual and a Classic.');
  }

  return out;
}

/**
 * R19b — the bundle consequence is WORDS, NEVER A FIGURE. This surface never
 * computes a discount amount (R22, KTD-H), so the line names the consequence in
 * prose and quotes no number for it. It is also only offered when the count
 * genuinely drops: breaking a Kit and keeping both pieces beside a cover is
 * still a trio, and inventing a loss there would be a scare, not a warning.
 */
export function bundleConsequence(before, after) {
  const was = Number(before?.complete) || 0;
  const now = Number(after?.complete) || 0;
  if (now >= was) return null;
  return 'It also ends the bundle your Getting Started Kit is part of — checkout works any discount out from what your cart holds at the time.';
}

// ═══════════════════════════════════════════════════════════════════════════
// U10 — "INCLUDED AT NO COST" (R13, R13a, R25, R26)
//
// THIS BLOCK RENDERS A VERDICT. IT DOES NOT COMPUTE ONE.
//
// `inclusionsFor()` (U3, eg-bundle-nudge.js) is the single source of the
// verdict, and it mirrors `orderQualifies()`'s branch order exactly — Kit,
// then subscription, then composition. `inclusionsCopy` below is a pure copy
// switch over the `{subscribed, trial}` it hands back: it never sees a line, a
// quantity, a cadence or a price, which is what stops a second display-side
// rule growing up beside the first. Two sources for one number is how the old
// bundle-pricing display drifted (U9's execution note, and it applies here).
//
// So there is NO PRICE AND NO THRESHOLD anywhere in this block. The rule is
// composition (OQ1, 2026-07-28) and nothing about it is expressible in money.
// "Six weeks" is spelled out for the same reason: a figure on this surface
// would read as a price, and there is none to read.
//
// ⚠ THE ASYMMETRY THAT TRIPS PEOPLE, STATED ONCE:
//
//   Renew IS an inclusion of subscribing. Renew is NOT a component of the
//   Getting Started Kit (R18b, OQ7, CONCEPTS.md).
//
// A Kit in the cart lands in the TRIAL state, so the trial sentence is the one
// a Kit buyer reads — and it therefore names the community and nothing else. A
// Kit buyer is not being promised a course. The subscribed sentence is the only
// place Renew appears, because subscribing is the only thing that confers it.
// ═══════════════════════════════════════════════════════════════════════════

/** R13 — the three states, and the only sentences this surface can emit. */
export const INCLUSIONS_TEXT = {
  // Kyle, design pass 2026-07-30 — say what Renew IS: the course lives IN the
  // community, so the sentence names that relationship instead of listing the
  // two as separate items. The DURATION moved to the note (below), so the body
  // names only what you get.
  subscribed: 'The community experience and the Renew course.',
  // R18b — no course and no Renew here: a Kit buyer is who reads this. The
  // DURATION moved to the heading (below), so the body only names the thing.
  trial: 'Access to the Evergreen Community.',
};

/**
 * The caption under the body. Both states have one now (Kyle, 2026-07-30 —
 * previously trial-only, and the subscribed state's duration was tacked onto
 * the end of its sentence instead). Each state's three lines therefore say one
 * thing each: the heading what it is, the body what you get, the note how long
 * it lasts.
 */
export const INCLUSIONS_NOTE = {
  subscribed: 'Ongoing for as long as you subscribe',
  trial: 'Ongoing membership free with any Classic subscription',
};

/**
 * The heading, which is state-driven for the same reason the sentence is
 * (Kyle, 2026-07-30). It was a literal in the Liquid, which made a SUBSCRIBER
 * read "Free six-week trial" above a sentence saying they have it ongoing —
 * the one state where the word "trial" is flatly wrong. Emitting it from here
 * means the two lines can never again describe different states, because one
 * switch produces both.
 *
 * Leadership will want to retune this copy; both maps are the one place to do
 * it, and neither is duplicated in the markup.
 */
export const INCLUSIONS_HEADING = {
  subscribed: 'The Evergreen Community',
  trial: 'Your six-week trial',
};

/**
 * The verdict from `inclusionsFor()`, phrased (R13).
 *
 * `subscribed` is checked BEFORE `trial` on purpose: a subscription always
 * qualifies (branch b), so both flags are true for a subscriber, and the
 * ongoing copy has to win over the six-week copy. A shopper who is already
 * subscribed must never be told they have six weeks — they have it for as long
 * as they subscribe, which is what their own note says instead.
 *
 * A junk or missing verdict degrades to 'none' rather than throwing — the same
 * tolerance rule eg-configurator-state.js and eg-bundle-nudge.js keep, because
 * a shopper mid-build is never shown a stack trace.
 */
export function inclusionsCopy(inclusions) {
  const verdict = inclusions !== null && typeof inclusions === 'object' ? inclusions : {};
  if (verdict.trial !== true) return { state: 'none', heading: '', text: '', note: '' };
  if (verdict.subscribed === true) {
    return {
      state: 'subscribed',
      heading: INCLUSIONS_HEADING.subscribed,
      text: INCLUSIONS_TEXT.subscribed,
      note: INCLUSIONS_NOTE.subscribed,
    };
  }
  return {
    state: 'trial',
    heading: INCLUSIONS_HEADING.trial,
    text: INCLUSIONS_TEXT.trial,
    note: INCLUSIONS_NOTE.trial,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// DOM MORPH (Kyle, design pass 2026-07-30 — the flicker fix). Renderers keep
// building fresh trees exactly as before, but nothing calls `replaceChildren`
// on a live list any more: `morphChildren` diffs the fresh tree against the
// live one and applies only what changed. Nodes that match by KEY survive in
// place — their `<img>`s never re-decode, their CSS insertion animations
// never restart, focus never leaves them (so `withFocusKept` restores
// nothing and `revealFocusedCard` has no focusin to scroll on), and the
// column's scroll position cannot jump.
//
// KEYS. An element's identity is, in order: `data-eg-key`, its `id`, its
// `data-eg-section`, else tagName+position. Every renderer stamps a stable
// key on anything whose siblings share a tag (cells carry their input's id;
// fieldsets their section id; accessory rows their variantId). The hero
// image's key IS its src on purpose: a genuinely new photo replaces the
// node, which is exactly what re-triggers the CSS entrance fade (R5) —
// same photo, same node, no fade.
//
// LISTENERS. A reused node keeps its ORIGINAL listeners; a brand-new node
// arrives with the fresh ones its renderer just bound. That is only sound
// because every render-built closure captures nothing but stable identifiers
// (variantIds, section ids, the `configurator` reference) and reads live
// state when invoked — audited 2026-07-30, and the two violations found
// (the remove button's positional `ordinal`, the thumb pick's stage `key`)
// were fixed in the same sitting: the remove button now derives the ordinal
// at click time, and a thumb's key includes the stage key so a step change
// always replaces the node.
//
// KEPT NODES. An element carrying `data-eg-keep` (the static "Add another…"
// buttons that live in the Liquid, reparented once into the section footer)
// is invisible to the diff: never matched, never moved, never removed.
//
// `checked`/`disabled`/`value` are synced as PROPERTIES — the renderers set
// them as properties on the detached tree, and attributes would only carry
// the defaults. The one class the diff must not strip is
// `eg-configurator__pulse`: it is toggled by `highlightSubscribe` OUTSIDE
// any render, so the fresh tree never knows about it.
// ═══════════════════════════════════════════════════════════════════════════

const MORPH_PULSE_CLASS = 'eg-configurator__pulse';

function morphKey(node, index) {
  if (node.nodeType !== Node.ELEMENT_NODE) return `#text:${index}`;
  return (
    node.getAttribute('data-eg-key') ||
    node.id ||
    node.getAttribute('data-eg-section') ||
    `${node.tagName}:${index}`
  );
}

function morphSyncElement(live, next) {
  const hadPulse = live.classList.contains(MORPH_PULSE_CLASS);
  for (const attr of next.attributes) {
    if (live.getAttribute(attr.name) !== attr.value) live.setAttribute(attr.name, attr.value);
  }
  for (const attr of [...live.attributes]) {
    if (!next.hasAttribute(attr.name)) live.removeAttribute(attr.name);
  }
  if (hadPulse) live.classList.add(MORPH_PULSE_CLASS);
  for (const prop of ['checked', 'disabled', 'value']) {
    if (prop in next && live[prop] !== next[prop]) live[prop] = next[prop];
  }
  if (live.hidden !== next.hidden) live.hidden = next.hidden;
}

function morphNode(live, next) {
  if (live.nodeType === Node.TEXT_NODE) {
    if (live.nodeValue !== next.nodeValue) live.nodeValue = next.nodeValue;
    return;
  }
  morphSyncElement(live, next);
  morphChildren(live, [...next.childNodes]);
}

/** True when `live` can be updated in place to become `next`. */
function morphCompatible(live, next) {
  if (live.nodeType !== next.nodeType) return false;
  if (live.nodeType !== Node.ELEMENT_NODE) return true;
  return live.tagName === next.tagName;
}

export function morphChildren(parent, nextNodes) {
  const liveNodes = [...parent.childNodes].filter(
    (node) => !(node.nodeType === Node.ELEMENT_NODE && node.hasAttribute('data-eg-keep'))
  );
  const liveByKey = new Map();
  liveNodes.forEach((node, index) => {
    const key = morphKey(node, index);
    if (!liveByKey.has(key)) liveByKey.set(key, node);
  });

  // Every live node not claimed by the loop below is removed at the end —
  // including duplicate-key nodes that never made the map and matches that
  // turned out tag-incompatible. Nothing may linger.
  const claimed = new Set();
  let cursor = null;
  // A DocumentFragment must never reach the keyed walk (adversarial review,
  // 2026-07-30): inserting one unwraps it, leaving `cursor` pointing at a
  // drained, parentless node whose `nextSibling` is null — every later item
  // would silently append at the end. `radioCell`'s disabled-option guard
  // returns empty fragments, so filter them here rather than trusting every
  // future caller to remember.
  nextNodes = nextNodes.filter((node) => node.nodeType !== Node.DOCUMENT_FRAGMENT_NODE);
  nextNodes.forEach((next, index) => {
    const key = morphKey(next, index);
    const match = liveByKey.get(key) ?? null;
    let placed;
    if (match && !claimed.has(match) && morphCompatible(match, next)) {
      claimed.add(match);
      morphNode(match, next);
      placed = match;
    } else {
      placed = next;
    }
    const ref = cursor ? cursor.nextSibling : parent.firstChild;
    if (placed !== ref) parent.insertBefore(placed, ref);
    cursor = placed;
  });

  for (const node of liveNodes) {
    if (!claimed.has(node)) node.remove();
  }
}

/**
 * PostHog / analytics dispatch (U22 amendment, 2026-09-02). This module has
 * no declaring element to hang `data-eg-event` off — the events fire from
 * JS state changes, not clicks on markup — so it dispatches a DOM
 * CustomEvent instead of importing eg-analytics.js. eg-analytics.js listens
 * for `eg:track` on `document` and relays to whatever sink exists
 * (gtag/dataLayer/posthog). Keeping this an event, not an import, keeps the
 * configurator out of theme/tests/module-import-map.test.js's graph.
 *
 * NO PII (KTD11): every call site below passes only counts, booleans, ids
 * and enums. FAILS SILENT: analytics must never break the converge-commit
 * loop, so a dispatch failure is swallowed, never surfaced.
 */
function track(name, params) {
  try {
    document.dispatchEvent(new CustomEvent('eg:track', { detail: { name, params: params || {} } }));
  } catch {
    /* analytics must never break the storefront */
  }
}

function initConfigurator(root) {
  const dialog = root.querySelector(SELECTORS.dialog);
  const dataEl = document.getElementById('eg-id-map');
  if (!dialog || !dataEl || typeof dialog.showModal !== 'function') return;

  let rawMap;
  try {
    rawMap = JSON.parse(dataEl.textContent);
  } catch (error) {
    console.error('[eg-configurator] unreadable id map', error);
    return;
  }

  const el = Object.fromEntries(
    Object.entries(SELECTORS).map(([key, selector]) => [key, root.querySelector(selector)])
  );

  /** Each repeating kind's shell: the list U6 fills, and the persistent "add
   * another" button that is ALSO the R26 focus target when the last instance
   * of its kind is removed. */
  const groups = Object.fromEntries(
    Object.entries(GROUP_FOR_KIND).map(([kind, name]) => {
      const group = root.querySelector(`[data-eg-section-group="${name}"]`);
      return [
        kind,
        {
          group,
          list: group ? group.querySelector('[data-eg-section-list]') : null,
          add: group ? group.querySelector(`[data-eg-section-add="${name}"]`) : null,
        },
      ];
    })
  );

  /** U3 (2026-10-06) — where the Liquid put the waitlist widget, so the
   * renderer can put it back. The widget is a SINGLE live node on purpose
   * (D5): eg-capture.js bound it at page load, so the text she has typed and
   * the thank-you panel it swaps in are DOM state no renderer owns. It goes
   * home (and hidden) the moment no cover group is between runs. */
  const coverWaitlistHome = el.coverWaitlist ? el.coverWaitlist.parentElement : null;

  const idMap = parseIdMap(rawMap);

  /** R6 — the insets are the SELECTED VARIANT's own photos then the product's
   * shared ones. `eg-id-map.liquid` already emits `images` in exactly that
   * order per variant, so a selected variant needs no composition here. */
  const galleries = new Map();
  const remember = (entry) => {
    if (entry && entry.variantId) galleries.set(entry.variantId, entry);
  };
  for (const cover of idMap.covers) for (const way of cover.colorways) remember(way);
  for (const cadence of idMap.classic.cadences) remember(cadence);
  remember(idMap.annual);
  for (const accessory of idMap.accessories) remember(accessory);

  const sharedByKind = {
    cover: sharedAcrossProducts(idMap.covers.map((cover) => cover.colorways)),
    // Cadences are variants of ONE product, so the plain intersection is the
    // right question here.
    classic: sharedImages(idMap.classic.cadences),
    annual: idMap.annual ? idMap.annual.images : [],
    // Each accessory is its own product with one variant, so "shared across
    // all of them" intersects to nothing in the same way. Same treatment.
    accessory: sharedAcrossProducts(idMap.accessories.map((accessory) => [accessory])),
  };

  const cartRoot = () => window.Shopify?.routes?.root ?? '/';

  /**
   * F8 — A 200 IS NOT PROOF YOU GOT THE CART.
   *
   * This used to end `Array.isArray(payload?.items) ? payload.items : []`, so a
   * 200 carrying JSON that is not a cart — a proxy, an app's interstitial, a
   * shape change — read as A CART WITH NOTHING IN IT. At open that renders an
   * empty build and, before F8, drove a removal of everything the shopper had:
   * the same hole as a failed read, arrived at through a request that
   * "succeeded". (docs/solutions/developer-experience/http-200-is-not-proof-
   * you-got-the-real-page.md; this store also serves a password page at 200.)
   *
   * So a missing `items` array THROWS, which is the honest answer — we did not
   * get the cart — and it lands on the caller's catch, where a failed read
   * already belongs. `items: []` from a real cart payload is untouched: an
   * empty cart is a fact, and this surface must still open on one.
   */
  async function readCartItems() {
    const response = await fetch(`${cartRoot()}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`cart.js responded ${response.status}`);
    const payload = await response.json();
    if (!Array.isArray(payload?.items)) throw new Error('cart.js responded 200 with no items array');
    return payload.items;
  }

  /** One operation. Non-2xx THROWS, which is how `convergeCommit` learns the
   * cart refused — though NOT that the cart stood still; see the loop's catch.
   * The envelope comes from `cartRequestFor`, which is what keeps adds on the
   * stock-enforcing line-item form (finding C). */
  async function postCartOp(op) {
    const { path, body } = cartRequestFor(op);
    const response = await fetch(`${cartRoot()}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`${path} responded ${response.status}`);
    return response.json().catch(() => null);
  }

  let configurator = createConfigurator(rawMap);
  /** Obligation 5 — the Kit units HYDRATION reported: what the shopper was
   * shown when they confirmed, never a number recomputed at commit time. */
  let hydratedKitUnits = 0;
  /** KTD-D's read-only register (an off-map plan on a scoped line). Captured so
   * it is never silently editable, and DISPLAYED by `renderReadOnly` (U9) —
   * outside the itemized breakdown, because R28 reserves that list for exactly
   * the lines the total sums. */
  let readOnlyLines = [];
  let scopedLinesAtOpen = 0;
  /**
   * F8 — CAN THIS SURFACE VOUCH FOR THE CART IT SHOWED THE SHOPPER? True only
   * when the open read RESOLVED and `hydrate()` turned it into the build they
   * then edited. It is what decides whether a line missing from their build is
   * an instruction to remove it or merely a line they were never shown, so it
   * starts false and is set from events, never from a default.
   *
   * Distinct from `cartEmptyAtOpen` below, and the two must not be confused:
   * that one says the cart HELD NOTHING, this one says WE KNOW WHAT IT HELD.
   * "Empty" and "unknown" looking identical is precisely what F8 was.
   */
  let cartVouchedAtOpen = false;
  /** bundle_status_shown (2026-09-02) — renderBundle runs on every render, so
   * this remembers the last status fired and only tracks on a real change
   * (spec's own warning: "needs care not to fire on every render"). */
  let lastBundleSignature = null;
  /** R15 — which discard register U9 uses is keyed on the cart AT OPEN. */
  let cartEmptyAtOpen = true;
  let openSignature = '';
  /** R2/R23 — how they got here decides where closing and the confirmation's
   * secondary action send them. Read off the shell, never hardcoded. */
  let arrivedByUrl = false;
  const urlArrivalExit = dialog.getAttribute('data-eg-url-arrival-exit') || '/';

  const money = (cents) => formatMoney(cents, idMap.currencySymbol);

  // ──────────────────────────────────────────────────── which surface is up

  const SURFACES = ['build', 'confirm', 'partial-commit', 'confirmation'];
  const OVERLAY_SURFACES = ['confirm', 'partial-commit'];
  let surface = 'build';

  /**
   * THE SINGLE PLACE THAT DECIDES WHICH VIEW OR SURFACE IS SHOWING.
   *
   * U9 owns the COPY and the flows for three of these — discard-on-close, the
   * Kit-break confirm and the partial-commit report all populate the shared
   * `[data-eg-confirm]` slot, and the post-commit confirmation fills the
   * sibling view. What U6 owns is the routing: which one is up, and that a
   * stall can never fall through to success (obligation 9, AE16).
   */
  function showSurface(name, detail = {}) {
    surface = SURFACES.includes(name) ? name : 'build';
    const overlay = OVERLAY_SURFACES.includes(surface);
    if (el.viewBuild) el.viewBuild.hidden = surface === 'confirmation';
    if (el.viewConfirmation) el.viewConfirmation.hidden = surface !== 'confirmation';
    // The shared overlay carries all THREE of U9's in-modal states; the message,
    // consequences, cart link and buttons are painted by whichever opened it.
    if (el.confirm) el.confirm.hidden = !overlay;
    // R26 — `<dialog>` traps focus inside the dialog, but not inside the
    // overlay. Without this a Tab from the confirm card walks straight into the
    // build's radios, which are underneath a scrim and are exactly what the
    // shopper is being asked about.
    if (el.viewBuild) el.viewBuild.inert = overlay;
    if (surface === 'partial-commit') {
      // The modal stays open and local state stays intact (AE16). The commit
      // button stays disabled because there is no retry on this path.
      if (el.submit) el.submit.disabled = true;
      console.warn('[eg-configurator] commit stalled', detail);
    }
    if (surface === 'confirmation') {
      paintConfirmation();
      // R26 — focus lands on the heading, so the shopper hears what happened
      // before being handed an action.
      if (el.confirmationHeading) el.confirmationHeading.focus();
    }
    return surface;
  }

  // ───────────────────────────────── U9: the shared confirm/report overlay
  //
  // THE U12 WALKTHROUGH SCRIPT. Everything below this line is DOM behaviour a
  // node test cannot see, so it is enumerated here rather than left to be
  // rediscovered. Walk each on BOTH breakpoints; the Kit-break text is checked
  // against a real Kit cart on the prod preview.
  //
  //   W1  Close with NO local changes → closes immediately, no prompt at all.
  //   W2  Close with local changes, cart EMPTY at open → "Leave without saving
  //       your build?" / [Leave] [Keep building]. Build a full trio first: the
  //       register must NOT switch, because nothing reached the cart (R15).
  //   W3  Close with local changes, cart NOT empty at open → "Close without
  //       updating your cart." / [Discard changes] [Keep building].
  //   W4  [Keep building] returns to the build with every selection intact and
  //       focus back on the control that was focused before the prompt.
  //   W5  Discarding from a `/pages/build-your-planner` arrival routes to
  //       /how-it-works; from a CTA arrival it just closes (R2).
  //   W6  Escape with the overlay up declines the overlay; Escape with the
  //       build up runs the discard prompt. Neither can be sidestepped.
  //   W7  Kit in cart: exactly two ticked sections, one Classics, one Annual,
  //       and NO Kit row anywhere (R18/AE7).
  //   W8  Edit a Kit-origin selection → the confirm fires with R19's wording,
  //       the live price line and the community verdict. The radio visually
  //       reverts BEFORE the card appears.
  //   W9  Decline → the tick comes back, nothing reaches the cart. Confirm →
  //       the edit lands, the Kit line goes at commit (AE7).
  //   W10 Edit a NON-Kit selection while a Kit is in the cart → no confirm
  //       (AE7a). Adding a second Classics section is not breaking the Kit.
  //   W11 Remove a section → focus lands on the next remaining section's first
  //       control, or on "Add another …" when none remain — never `<body>`,
  //       which escapes the dialog's focus trap (R26).
  //   W12 Force a stall (throttle /cart/change.js to 4xx) → the partial-commit
  //       card, the /cart link, "Back to my build", and NO retry. The success
  //       confirmation must never appear, the modal stays open and the build
  //       still holds every selection (AE16).
  //   W13 Commit cleanly → the confirmation REPLACES the build view (nothing
  //       of the build visible behind it), focus lands on the heading, and the
  //       heading is announced.
  //   W14 Confirmation secondary reads "Return to {page}" from a CTA arrival
  //       and "Go to the homepage" from a /build arrival (AE8).
  //   W15 A cart line carrying an off-map selling plan → the "Also in your
  //       cart" note appears under the breakdown, names the line, and the line
  //       is NOT in the itemized list and NOT in the total (KTD-D, R28).
  //   W16 Tab through each of the four surfaces with the screen reader on:
  //       every control reachable, nothing behind the scrim reachable.
  //
  // U10's additions to the same script — "Included at no cost" (R13, R25, R26).
  // Everything about the scroll and the highlight is browser-only: a real
  // scroll container, a real `prefers-reduced-motion` media query, real focus.
  //
  //   W17 An Annual + any Classic, one-off → the section appears reading "Six
  //       weeks in the Evergreen Community." with "Make it ongoing" under it.
  //       An Annual alone, a Classic alone, or any pile of accessories at any
  //       total → the section is HIDDEN (AE15). Verify the accessory case at a
  //       total well above $47: there is no threshold to trip.
  //   W18 Tick any Classics subscription box → the sentence swaps to the Renew
  //       course and the community, ongoing, and the link DISAPPEARS. Untick →
  //       it comes back. Nothing else on the surface moves.
  //   W19 A Kit in the cart with nothing else → the section shows the six-week
  //       sentence. It must NOT name the Renew course: Renew is an inclusion of
  //       subscribing, never a component of the Kit (R18b/OQ7).
  //   W20 Follow "Make it ongoing" BY MOUSE → the right column scrolls up to
  //       the Classics section, the subscription checkbox's label pulses twice,
  //       and the stage has swapped to that section's image.
  //   W21 Follow it BY KEYBOARD (Tab to it, Enter/Space) → the same scroll and
  //       highlight, AND focus is on the checkbox itself, so Space immediately
  //       ticks it. Confirm focus is NOT left on the link and NOT on `<body>`.
  //   W22 Press it a SECOND time without changing anything → the pulse restarts.
  //       (Without the reflow it would only ever animate once.)
  //   W23 With `prefers-reduced-motion: reduce` set at the OS level (AE9): the
  //       scroll still arrives (instant, not smooth), the checkbox still gets a
  //       visible ring, the stage still updates. A reduced-motion shopper who
  //       cannot tell what was highlighted is the bug this exists to prevent.
  //   W24 Toggle the OS setting WITH the modal open, then press again — the new
  //       preference is honoured on the next press, not the next page load.
  //   W25 With several Classics sections, the link targets the first UNTICKED
  //       one. Tick that one and press again → it moves to the next unticked.
  //   W26 With the screen reader on, a build crossing into the trial state
  //       announces "included at no cost, six weeks …" in the same sentence as
  //       the rest of the build; the section itself exposes no control other
  //       than the link (R13 — read-only).
  //   W27 Mobile (R24): the inclusions strip sits outside the horizontal rail,
  //       and following the link scrolls the RAIL to Classics. Check the ring
  //       is not clipped by the rail's overflow.

  /** The control that was focused when the overlay opened, by ID — a re-render
   * replaces the node, so holding the element itself would restore focus to a
   * detached radio and drop the shopper on `<body>` (R26/WCAG 2.4.3). */
  let confirmReturnFocusId = null;

  /**
   * Paint and raise the shared slot. One function for all three states, so the
   * a11y contract (announce, focus in, focus back) is written once and cannot
   * drift between them.
   */
  function openConfirm({ message, consequences = [], cartLink = false, primary = null, secondary = null, surfaceName = 'confirm', detail = {} }) {
    if (!el.confirm) return;
    confirmReturnFocusId = document.activeElement?.id ?? null;

    if (el.confirmMessage) el.confirmMessage.textContent = message;

    if (el.confirmConsequences) {
      el.confirmConsequences.hidden = consequences.length === 0;
      el.confirmConsequences.replaceChildren(
        ...consequences.map((text) => {
          const li = document.createElement('li');
          li.className = 'eg-configurator__confirm-consequence';
          li.textContent = text;
          return li;
        })
      );
    }
    if (el.confirmCartLink) el.confirmCartLink.hidden = !cartLink;

    for (const [node, spec] of [
      [el.confirmPrimary, primary],
      [el.confirmSecondary, secondary],
    ]) {
      if (!node) continue;
      node.hidden = spec === null;
      node.replaceChildren();
      node.onclick = null;
      if (spec === null) continue;
      node.textContent = spec.label;
      node.onclick = spec.onClick;
    }

    showSurface(surfaceName, detail);

    // R26 — the first real control in the card, so a keyboard shopper lands
    // inside the thing that just took over the screen. The VISIBLE first
    // control: the /cart link lives inside a `[hidden]` paragraph on the two
    // states that do not offer it, and focusing a display:none element fails
    // silently — which would drop the shopper on `<body>`, outside the overlay
    // they are being asked about (WCAG 2.4.3).
    const target = [...el.confirm.querySelectorAll('a[href], button')].find(
      (node) => !node.hidden && node.closest('[hidden]') === null
    );
    if (target) target.focus();

    if (el.announce) el.announce.textContent = [message, ...consequences].join(' ');
  }

  /** Dismiss the overlay and put focus back where it came from. */
  function closeConfirm({ restoreFocus = true } = {}) {
    showSurface('build');
    if (!restoreFocus) return;
    const restored = confirmReturnFocusId ? document.getElementById(confirmReturnFocusId) : null;
    if (restored && typeof restored.focus === 'function') restored.focus();
    else if (el.close) el.close.focus();
    confirmReturnFocusId = null;
  }

  /**
   * R15 — the close-with-unsaved-changes prompt, in the register keyed on
   * whether the cart was empty AT OPEN (never at close: see `discardCopy`).
   */
  function discardConfirm({ cartEmptyAtOpen: emptyAtOpen, onDiscard }) {
    const copy = discardCopy(emptyAtOpen);
    openConfirm({
      message: copy.message,
      primary: {
        label: copy.confirmLabel,
        // Focus is not restored: the modal is going away, and the element it
        // would go back to goes with it.
        onClick: () => {
          // configurator_discard (2026-09-02) — abandonment: the shopper
          // closed the modal and threw away local edits.
          track('configurator_discard', { cart_empty_at_open: emptyAtOpen === true });
          closeConfirm({ restoreFocus: false });
          onDiscard();
        },
      },
      secondary: { label: copy.cancelLabel, onClick: () => closeConfirm() },
    });
  }

  /**
   * R19/R19a/R19b — the Kit-break confirm, with its consequences computed live
   * from the projected build. See the module block above for why the verdict is
   * READ from `inclusionsFor()` and why no threshold appears anywhere.
   */
  function breakConfirm(pending, api) {
    // The projection reads the pending break OFF STATE rather than off the
    // argument, so the confirm and the change it describes can never come from
    // two different records of what the shopper just did. `pending` stays in the
    // signature because it is the caller's contract that a break was raised.
    void pending;
    const current = api.state();
    const projection = projectKitBreak(current);
    // Post-break the Kit line is gone, so the projected build is the ordinary
    // lines plus whatever pieces survive the edit, and NO Kit line.
    const projectedLines = [...current.lines, ...projection.pieces];
    const inclusions = inclusionsFor(projectedLines, []);
    const consequences = kitBreakConsequences(projection, inclusions, money);
    const bundleLine = bundleConsequence(
      bundleStatus(current.lines, current.kitLine ? [current.kitLine] : []),
      bundleStatus(projectedLines, [])
    );
    if (bundleLine) consequences.push(bundleLine);

    openConfirm({
      message: KIT_BREAK_MESSAGE,
      consequences,
      primary: {
        label: 'Yes, change it',
        onClick: () => {
          const next = api.confirmBreak();
          closeConfirm();
          render(next);
        },
      },
      secondary: {
        label: 'Keep my Getting Started Kit',
        onClick: () => {
          // R19 — declining reverts the edit. Nothing was mutated when the
          // break was raised, so this only drops the pending record; the
          // re-render below is what puts the radio back.
          // configurator_kit_break_declined (2026-09-02) — the shopper read
          // what breaking the Kit costs them and backed out.
          track('configurator_kit_break_declined');
          const next = api.cancelBreak();
          closeConfirm();
          render(next);
        },
      },
    });
  }

  /**
   * AE16 — the partial-commit report. The one path where the shopper's cart is
   * left wrong, so it gets real copy: an honest sentence naming the failure, a
   * link to /cart, and DELIBERATELY NO RETRY. "Back to my build" leaves local
   * state intact with the modal open, exactly as AE16 requires.
   */
  function partialCommitConfirm(result) {
    const copy = partialCommitCopy(result.reason, result.appliedOpKeys?.length ?? 0);
    // configurator_partial_commit (2026-09-02) — THE clearest struggle signal:
    // the shopper's cart write partly failed and they land on a dead-end
    // surface with no retry (AE16).
    track('configurator_partial_commit', {
      reason: result.reason,
      authority: result.authority,
      applied_count: result.appliedOpKeys?.length ?? 0,
      refused_count: result.refusedOpKeys?.length ?? 0,
    });
    openConfirm({
      surfaceName: 'partial-commit',
      detail: {
        reason: result.reason,
        // F8 — which of the three states the run was in. First question worth
        // asking of any stall report that mentions a cart nobody expected.
        authority: result.authority,
        applied: result.appliedOpKeys,
        refused: result.refusedOpKeys,
      },
      message: copy.message,
      cartLink: true,
      secondary: { label: 'Back to my build', onClick: () => closeConfirm() },
    });
  }

  /**
   * R23 — the post-commit confirmation, in the 404 page's centred register and
   * identical on both breakpoints. The secondary's arrival split (AE8) is
   * decided by `confirmationSecondary` from what was captured at open.
   */
  function paintConfirmation() {
    const heading = 'Your build is in your cart.';
    const body = 'Everything you picked is in there together, ready whenever you are.';
    if (el.confirmationHeading) el.confirmationHeading.textContent = heading;
    if (el.confirmationText) el.confirmationText.textContent = body;

    const secondary = confirmationSecondary({
      arrivedByUrl,
      pageName: pageNameFromTitle(document.title),
      homeHref: dialog.getAttribute('data-eg-home-href') || cartRoot(),
      homeLabel: dialog.getAttribute('data-eg-home-label') || '',
      returnHref: window.location?.pathname ?? cartRoot(),
    });
    if (el.confirmationSecondary) {
      el.confirmationSecondary.textContent = secondary.label;
      el.confirmationSecondary.href = secondary.href;
      el.confirmationSecondary.onclick = secondary.closesModal
        ? (event) => {
            // A CTA arrival is already standing on the page it would navigate
            // to; closing the modal IS the return, and a navigation would only
            // throw away their scroll position.
            event.preventDefault();
            // Through `closeDialog`, so D3's backstop reads this as asked-for:
            // the build IS dirty by now (it just went to the cart), and an
            // unannounced close is the one thing that backstop undoes.
            closeDialog();
          }
        : null;
    }
    if (el.announce) el.announce.textContent = `${heading} ${body}`;
  }

  // ────────────────────────────────────────────────────────────── the stage

  /** R5 — the stage follows scroll AND focus. `focusStep` is the pattern this
   * file has always used and it is carried forward, not replaced: a browser
   * only auto-scrolls a focused control into view when it is already
   * off-screen, so a keyboard shopper tabbing through VISIBLE controls
   * generates no scroll event and would otherwise never see the stage move. */
  let step = { kind: 'cover', variantId: null, label: 'Your build' };

  /**
   * What the stage currently HOLDS, as a key, or null for "nothing painted yet
   * / repaint unconditionally". See `renderStage` for why this exists.
   *
   * Reset in `open()`, so a second open always repaints: a `<dialog>` keeps its
   * DOM between opens, so re-opening on the same cover would otherwise find the
   * key unchanged and skip the paint — and with it U7's entrance fade, which is
   * keyed on insertion. Reopening is a legitimate stage change.
   */
  let paintedStageKey = null;

  /** What the hero box currently SHOWS (its resolved src, or a placeholder
   * marker). Separate from `paintedStageKey` on purpose (2026-07-30, flicker
   * fix): two different steps can resolve to the SAME photo, and a thumb
   * pick force-nulls the step key — in both cases the image is unchanged and
   * must not be torn down, because a fresh hero `<img>` restarts the 200ms
   * entrance fade and re-decodes the file. The fade now fires exactly when
   * the pixels change, which is what R5 meant all along. */
  let paintedHeroKey = null;

  /** Kyle, 2026-07-29 — the accessory a shopper picked via the stage circles.
   * PERSISTS across scroll-aways: leave the Accessories section, come back,
   * and the same accessory is still selected (comparing a bundle depends on
   * it). Reset only when a new modal session opens. */
  let pickedAccessoryId = null;

  function focusStep(kind, variantId, label) {
    step = { kind, variantId: variantId ?? null, label: label ?? '' };
    renderStage();
  }

  /**
   * Everything below is a pure function of `step`: `galleries` and
   * `sharedByKind` are built once from the id map at wiring and never mutated,
   * so the same step always paints the same stage.
   *
   * WHICH IS WHY IT IS GATED. `render()` calls this on every repaint, but the
   * stage only ever moves through `focusStep`, so stepping an accessory,
   * picking a cadence or ticking subscribe used to tear down and rebuild the
   * hero and every thumb for an identical result. The cost is not CPU: the fade
   * is keyed on INSERTION (see the note at the foot of this function), so a
   * fresh `.eg-configurator__hero-image` restarts the entrance animation and
   * re-decodes the image — visibly, on a click that had nothing to do with the
   * stage. `label` is in the key alongside `kind`/`variantId` because it is the
   * alt text, and it is the part of a step that can change on its own.
   */
  /** Kyle, 2026-07-29 (second circles pass) — the shopper's hero pick WITHIN
   * the current step's gallery: clicking any circle swaps the hero to that
   * image. Keyed to the stage key, so re-aiming the stage (new step or
   * variant) drops back to that gallery's own first image. */
  let stageOverride = null;

  function renderStage() {
    const key = `${step.kind} ${step.variantId} ${step.label}`;
    if (key === paintedStageKey) return;
    if (stageOverride && stageOverride.key !== key) stageOverride = null;

    // A SOLD-OUT ENTRY NEVER AIMS THE STAGE (2026-07-31). A hydrated cart
    // line whose colorway has since sold out is still `selected` (R27 —
    // reflection wins), still focusable, and still stamped on the section's
    // data-eg-step-variant for the scroll-spy — so all three roads into
    // `focusStep` can arrive here carrying an out-of-stock variantId. Honour
    // the SELECTION, but show the product's shared imagery rather than
    // photographs of the exact thing she can no longer buy.
    const rawEntry = step.variantId === null ? null : galleries.get(step.variantId) ?? null;
    const entry = rawEntry && rawEntry.available === false ? null : rawEntry;
    let images = entry
      ? entry.images.length > 0
        ? entry.images
        : [entry.image].filter(Boolean)
      : sharedByKind[step.kind] ?? [];
    // Kyle, 2026-07-29 — the Accessories step with nothing picked yet shows
    // the FIRST accessory as the hero, not the shared fallback; the circles
    // below are the picker.
    if (step.kind === 'accessory' && !entry) {
      const first = idMap.accessories[0] ?? null;
      images = [first?.image ?? first?.images?.[0]].filter(Boolean);
    }

    const heroSrc = stageOverride?.src ?? images[0] ?? entry?.image ?? null;
    if (el.hero) {
      // SAME PHOTO, SAME NODE (2026-07-30, flicker fix) — the hero is only
      // rebuilt when the resolved image actually changes. Rebuilding is what
      // restarts the entrance fade, so the fade now marks a real photo
      // change and nothing else. The placeholder key carries the label so a
      // step with no imagery still repaints when its label differs.
      const heroKey = heroSrc ?? `placeholder:${step.label}`;
      if (heroKey !== paintedHeroKey) {
        // BLURRED BACKGROUND FILL (Kyle, 2026-07-29) — the same image twice:
        // a cover-fit, heavily blurred copy fills the whole stage behind the
        // contain-fit hero, so letterboxed space reads as the photo's own
        // ambience instead of an empty stage. Same src (already cached),
        // decorative only (empty alt + aria-hidden).
        const backdrop = imageNode(heroSrc, '', 'eg-configurator__hero-backdrop');
        backdrop.setAttribute('aria-hidden', 'true');
        el.hero.replaceChildren(backdrop, imageNode(heroSrc, step.label, 'eg-configurator__hero-image'));
        paintedHeroKey = heroKey;
      }
    }
    if (el.thumbs) {
      // EVERY circle is a button (Kyle, 2026-07-29, superseding the earlier
      // accessories-only picker and the original inert-inset rule): clicking
      // a circle swaps the hero to that image, and the clay ring marks
      // whichever image the hero is showing. On the Accessories step the
      // circles are one-per-accessory (the pick persists across scroll-aways
      // via `pickedAccessoryId`); on every other step they are the current
      // gallery IN FULL — the hero's own image included, so a shopper can
      // always swap back.
      const accessoryStep = step.kind === 'accessory';
      const thumbItems = accessoryStep
        ? idMap.accessories.map((accessory) => ({
            src: accessory.image ?? accessory.images?.[0] ?? null,
            variantId: accessory.variantId,
            title: accessory.title,
          }))
        : images.map((src) => ({ src, variantId: null, title: null }));
      const selectedId = accessoryStep ? step.variantId ?? pickedAccessoryId : null;
      // The track scrolls horizontally; a rebuild must not throw the shopper
      // back to its start. (Morphed since 2026-07-30 — same-step repaints
      // keep every circle and its decoded image; the restore remains for the
      // genuine step-change rebuild.)
      const keptScrollLeft = el.thumbs.scrollLeft;
      morphChildren(
        el.thumbs,
        thumbItems.map((item) => {
          const li = document.createElement('li');
          li.className = 'eg-configurator__thumb';
          // The STAGE key is part of the circle's identity on purpose (morph
          // audit 2026-07-30): the pick handler's closure captures `key` and
          // `item`, so a circle must never survive into a different step's
          // gallery — a changed step changes every key and replaces every
          // node, closures included.
          li.dataset.egKey = `thumb-${key}-${item.variantId ?? item.src}`;
          const selected = accessoryStep ? item.variantId === selectedId : item.src === heroSrc;
          if (selected) li.classList.add('eg-configurator__thumb--selected');
          const image = imageNode(item.src, item.title ?? step.label, 'eg-configurator__thumb-image');
          const pick = document.createElement('button');
          pick.type = 'button';
          pick.className = 'eg-configurator__thumb-button';
          pick.setAttribute('aria-label', item.title ? `Show ${item.title}` : 'Show this photo');
          pick.setAttribute('aria-pressed', selected ? 'true' : 'false');
          pick.addEventListener('click', () => {
            if (accessoryStep && item.variantId !== null) {
              pickedAccessoryId = item.variantId;
              stageOverride = null;
              focusStep('accessory', item.variantId, item.title);
              return;
            }
            stageOverride = { key, src: item.src };
            paintedStageKey = null;
            renderStage();
          });
          pick.appendChild(image);
          li.appendChild(pick);
          return li;
        })
      );
      el.thumbs.scrollLeft = keptScrollLeft;
    }
    // NO FADE HOOK HERE (R25). This used to toggle
    // `eg-configurator__stage--enter` on `[data-eg-stage]` with the usual
    // remove/reflow/add dance — against a class component-eg-configurator.css
    // has never styled, so it animated nothing and no test could see that. U7's
    // fade is keyed on INSERTION instead: `el.hero.replaceChildren` above builds
    // a brand-new `.eg-configurator__hero-image` (or `.eg-configurator__
    // placeholder`) every time the stage re-aims, and a CSS `animation` on a
    // freshly-inserted element runs without anything to trigger it. That is why
    // the fade has in fact been working, and why its `prefers-reduced-motion`
    // block — which keeps the swapped image and drops only the motion (AE9) —
    // is on those two selectors and not on the stage.

    // Last, so a throw above leaves the key stale and the next call repaints.
    paintedStageKey = key;
  }

  /**
   * R25 — the sticky footer's edge treatment. U7 authored
   * `.eg-configurator__footer--stuck` as a contract for this file to honour and
   * nothing ever added the class, so the hairline never appeared.
   *
   * THE TEST U7 NAMED CANNOT BE ASKED ANY MORE. It was "the footer's own
   * bounding rect no longer matches its in-flow position" — the ordinary
   * sticky-footer question, right while the whole build view scrolled. The view
   * is a fixed frame now (Kyle, 2026-07-29): `el.sections` (`SELECTORS.sections`
   * — `[data-eg-scroll]` since the design pass 2026-07-29 batch 2 moved the
   * header inside it; was `[data-eg-sections]` directly before that) is the
   * only scroll surface and the footer cannot travel, so that condition is
   * false forever rather than merely unimplemented.
   *
   * The AFFORDANCE is unchanged, so it is asked of the element that does now
   * scroll: content is still being cut off at this edge exactly when the
   * sections column is scrolled short of its own bottom, and that is the moment
   * a shopper needs the line. On mobile the same column is a horizontal rail
   * with `overflow-y: hidden`, so `scrollHeight === clientHeight` and the
   * hairline correctly stays off (R24).
   */
  function syncFooterEdge() {
    if (!el.footer || !el.sections) return;
    // The 1px slack absorbs sub-pixel layout: a column scrolled fully to the
    // bottom routinely lands a fraction short, and a hairline that flickers on
    // at the end of every scroll is worse than no hairline at all.
    const cutOff = el.sections.scrollHeight - el.sections.scrollTop - el.sections.clientHeight > 1;
    el.footer.classList.toggle('eg-configurator__footer--stuck', cutOff);
  }

  /**
   * Scroll-spy (R5, design pass 2026-07-29 batch 3): the stage now switches
   * at the HALFWAY point of the scroll container, not at "nearest the
   * leading edge" — a heading has to actually cross the container's own
   * midpoint before the step it belongs to takes over the stage, instead of
   * the stage jumping to whichever heading is merely closest to the top-left
   * corner (which fired the change far too early, before the shopper had
   * scrolled a section into real view).
   *
   * "PASSES THE MIDPOINT", MADE AXIS-AGNOSTIC WITHOUT A BREAKPOINT BRANCH.
   * Stated for the desktop column it is: the active section is the LAST
   * heading whose top edge is still above the container's own vertical
   * midpoint (`bounds.top + bounds.height / 2`). The mobile rail (R24) scrolls
   * the SAME container horizontally instead, where "top" never moves (one
   * row) and "midpoint" has to mean left-vs-width. Rather than branch on
   * breakpoint, both axes are summed on both sides: `offset` is the node's
   * combined distance from the container's top-left corner, `half` is the
   * combined half-extent of the container. On the desktop column every
   * node's `left` sits at ~the same value, so that term is ~constant across
   * nodes and cancels out of the comparison, leaving pure vertical
   * midpoint-crossing; on the mobile rail `top` is what's ~constant instead,
   * leaving pure horizontal midpoint-crossing. One formula, whichever axis is
   * actually scrolling decides the result.
   *
   * DIRECTION-AWARE, "A LITTLE OVER HALFWAY" BOTH WAYS (Kyle, 2026-07-29).
   * The trigger line depends on travel direction, so the flip lands just past
   * the midpoint in BOTH directions and can never flap at the boundary:
   *
   *   • Moving DOWN the list (scrolling forward): a section takes the stage
   *     once its HEADING has crossed a little past the container's midpoint —
   *     the line sits at 45% of the extent, so the heading is just beyond
   *     center when the flip happens.
   *   • Moving UP the list (scrolling backward): the EARLIER section takes
   *     the stage once its BOTTOM has come down a little past the midpoint —
   *     the line sits at 55%, and "last heading above the line" plus the
   *     80px inter-section gap means the flip lands as the previous
   *     section's bottom clears just past center.
   *
   * ONE AXIS, chosen by which way the container can actually scroll (vertical
   * column on desktop, horizontal rail on mobile — R24). The previous formula
   * summed both axes into one "half", which on desktop inflated the line by
   * half the column's WIDTH — that is why the stage flipped while headings
   * were still far below center (observed live: flipping at ~25% up).
   *
   * Among nodes whose leading edge has not crossed the line, the GREATEST
   * offset wins (most recently passed); nothing qualifying falls back to the
   * first node. A node carrying no variant never DOWNGRADES a same-kind step
   * that has one — scrolling inside the Accessories section must not reset a
   * circle-picked accessory back to the default.
   */
  let lastSpyPos = null;
  let lastSpyForward = true;
  /** A direction flip needs a REAL move, not a pixel (Kyle, 2026-07-29 — the
   * jumpy stage at the bottom of the column). Trackpad momentum and
   * rubber-band settling at a scroll extreme emit alternating 1px deltas; with
   * the flag flipping on every one of them the trigger line oscillated between
   * its forward and backward positions, so the winning section — and with it
   * the whole stage — flapped between Annual and Accessories while the shopper
   * was not really scrolling at all. */
  const SPY_DIRECTION_DEADBAND = 8;
  function stepFromScroll() {
    if (!el.sections) return;
    const vertical =
      el.sections.scrollHeight - el.sections.clientHeight >= el.sections.scrollWidth - el.sections.clientWidth;
    const pos = vertical ? el.sections.scrollTop : el.sections.scrollLeft;
    if (lastSpyPos !== null && Math.abs(pos - lastSpyPos) >= SPY_DIRECTION_DEADBAND) {
      lastSpyForward = pos > lastSpyPos;
      lastSpyPos = pos;
    } else if (lastSpyPos === null) {
      lastSpyPos = pos;
    }
    // Pinned at either end there is nothing left to decide, and deciding
    // anyway is what let a 1px bounce at the bottom re-aim the stage: hold
    // whatever the last real scroll chose.
    const maxPos = vertical
      ? el.sections.scrollHeight - el.sections.clientHeight
      : el.sections.scrollWidth - el.sections.clientWidth;
    if (maxPos > 0 && (pos <= 0 || pos >= maxPos - 1) && step.kind) return;

    const bounds = el.sections.getBoundingClientRect();
    const extent = vertical ? bounds.height : bounds.width;
    const line = extent * (lastSpyForward ? 0.45 : 0.55);
    const nodes = [...el.sections.querySelectorAll('[data-eg-step-kind]')];
    let best = null;
    let bestOffset = -Infinity;
    for (const node of nodes) {
      const box = node.getBoundingClientRect();
      const offset = vertical ? box.top - bounds.top : box.left - bounds.left;
      if (offset <= line && offset > bestOffset) {
        bestOffset = offset;
        best = node;
      }
    }
    if (!best) best = nodes[0] ?? null;
    if (!best) return;
    const kind = best.dataset.egStepKind;
    let variantId = best.dataset.egStepVariant || null;
    let label = best.dataset.egStepLabel || '';
    // Kyle, 2026-07-29 — returning to the Accessories section restores the
    // circle the shopper picked earlier, never the default.
    if (kind === 'accessory' && variantId === null && pickedAccessoryId !== null) {
      variantId = pickedAccessoryId;
      label = idMap.accessories.find((accessory) => accessory.variantId === variantId)?.title ?? label;
    }
    if (kind === step.kind && (variantId === step.variantId || variantId === null)) return;
    focusStep(kind, variantId, label);
  }

  /**
   * D1 — REVEAL THE CARD, NOT THE INPUT.
   *
   * With the containing block fixed (component-eg-configurator.css), a focused
   * control's scroll chain finally runs through the panel it lives in. It still
   * is not enough on its own: the control is `clip: rect(0 0 0 0)`, so its
   * visible rect is EMPTY and Chrome's own scroll-into-view has nothing to
   * reveal — measured at 390x844, two of the nine cover cards still sat wholly
   * or partly below the panel's bottom edge after focusing them, because the
   * browser either saw the 1px input as already visible or skipped the scroll
   * outright. The card is what the shopper needs on screen, so the card is what
   * gets asked for.
   *
   * `block: 'nearest'` scrolls the minimum required and does nothing at all
   * when the card is already fully visible, so this is silent on the common
   * path and never fights `highlightSubscribe`'s deliberate `block: 'center'`
   * (which runs after it). `inline: 'nearest'` leaves the mobile rail's
   * snap points alone for the same reason.
   *
   * Delegated rather than bound per control: sections are replaced wholesale on
   * every render (R8), and one listener on the scroller cannot go stale.
   */
  function revealFocusedCard(event) {
    const card =
      typeof event.target?.closest === 'function'
        ? event.target.closest('.eg-configurator__cell, .eg-configurator__subscribe-row, .eg-swatch')
        : null;
    if (!card || typeof card.scrollIntoView !== 'function') return;
    card.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  if (el.sections) el.sections.addEventListener('focusin', revealFocusedCard);

  let scrollPending = false;
  if (el.sections) {
    el.sections.addEventListener(
      'scroll',
      () => {
        if (scrollPending) return;
        scrollPending = true;
        requestAnimationFrame(() => {
          scrollPending = false;
          stepFromScroll();
          syncFooterEdge();
        });
      },
      { passive: true }
    );
  }

  // ─────────────────────────────────────────────────────────────── mutation

  /** A cheap, stable read of what the shopper has chosen — R15's "unsaved
   * changes" question, and the only honest way to answer it. */
  function buildSignature(state) {
    return JSON.stringify([
      state.sections.map((section) => [section.kind, section.coverVariantId ?? section.cadence ?? section.present, section.subscribe ?? null]),
      state.accessoryQuantities,
      state.kitBroken,
    ]);
  }

  function isDirty() {
    return buildSignature(configurator.state()) !== openSignature;
  }

  /**
   * Every click lands HERE and nowhere near the cart (R15): local state only,
   * then a re-render. Nothing this surface does touches the cart until commit.
   */
  function apply(nextState) {
    if (nextState.pendingBreak) {
      // R19 — state refused to mutate and raised a break instead. The re-render
      // comes FIRST and deliberately so: a native radio has already flipped
      // itself in the DOM by the time this runs, and repainting from the
      // (unmutated) state is what visually reverts it. Raising the confirm
      // afterwards also keeps `withFocusKept` from stealing focus back out of
      // the confirm card it is about to open (R26).
      render(nextState);
      breakConfirm(nextState.pendingBreak, configurator);
      return;
    }
    render(nextState);
  }

  // ────────────────────────────────────────────────────────────── rendering

  /** A re-render replaces the controls, which drops focus to `<body>` and
   * silently escapes the dialog's focus trap mid-purchase (R26/WCAG 2.4.3).
   * Control ids are derived from the section id and the option, so they are
   * stable across renders and focus can simply be put back. */
  function withFocusKept(paint) {
    const activeId = document.activeElement?.id ?? null;
    paint();
    if (!activeId) return;
    const restored = document.getElementById(activeId);
    // `preventScroll` (Kyle, 2026-07-29 — the bottom-of-column scroll bounce):
    // a bare focus() lets the browser's native scroll-into-view fire on a
    // 1x1 clipped input at the same moment the repaint resized the column's
    // tail (breakdown/total/nudge), and it then RACES revealFocusedCard's own
    // deliberate scrollIntoView — two competing scroll adjustments per click,
    // felt as a jump. Same hardening highlightSubscribe already carries;
    // revealFocusedCard stays the one deliberate scroll.
    if (restored && typeof restored.focus === 'function') restored.focus({ preventScroll: true });
  }

  function radioCell({ id, name, checked, disabled, onChange, onFocus, className, ariaLabel }, ...labelChildren) {
    // OUT OF STOCK NEVER REACHES THE DOM (Kyle, 2026-07-29 — superseding the
    // R9 greyed-and-labelled treatment, and the CSS display:none that briefly
    // replaced it). One guard in the single factory every option cell is built
    // by, so all five kinds obey it and no renderer has to know about stock.
    // An empty fragment is what callers append, so no call site changes.
    //
    // `disabled` already means "unpickable AND not the current selection"
    // (each caller passes `option.disabled && !option.selected`), so a variant
    // the shopper's own cart holds keeps rendering even once it sells out —
    // which is what keeps this surface an honest reflection of the cart, and
    // keeps a build from reading as "they removed it" on the destructive path.
    if (disabled) return document.createDocumentFragment();
    const input = document.createElement('input');
    input.type = 'radio';
    input.name = name;
    input.id = id;
    input.className = 'eg-configurator__radio';
    input.checked = checked;
    input.disabled = disabled;
    // The cover cells pass this so the product name rides on the radio's OWN
    // accessible name too ("Heirloom cover — Joe"), alongside the visible
    // per-product `<legend>` subheading `renderCoverSection` renders above the
    // grid — a control's own `<label>` wins the accessible-name computation
    // over an ancestor `<legend>`, so without this the legend's grouping would
    // never reach a screen reader. Optional: everything else on this surface
    // keeps its plain visible-label-only name.
    if (ariaLabel) input.setAttribute('aria-label', ariaLabel);
    input.addEventListener('change', onChange);
    if (onFocus) input.addEventListener('focus', onFocus);

    const label = document.createElement('label');
    label.className = className;
    label.setAttribute('for', id);
    label.append(...labelChildren);

    const cell = document.createElement('div');
    cell.className = 'eg-configurator__cell';
    // Morph key (2026-07-30): the input id is the cell's stable identity, so
    // the diff can keep this node — image, listeners, focus — across renders.
    cell.dataset.egKey = id;
    cell.append(input, label);
    return cell;
  }

  function textSpan(className, text) {
    const span = document.createElement('span');
    span.className = className;
    span.textContent = text;
    return span;
  }

  /** The stepper's +/− glyph (Kyle, design pass 2026-07-30) — the Phosphor
   * bold plus/minus, cloned from the <template data-eg-icon> pair the Liquid
   * inlines once (same `inline_asset_content` mechanism as the close button's
   * X). Falls back to the old text character if a template is ever missing,
   * so a stale cached module can't render an unlabelled empty button. */
  function qtyGlyph(kind, fallbackText) {
    const template = root.querySelector(`template[data-eg-icon="${kind}"]`);
    const icon = template?.content?.firstElementChild ?? null;
    return icon ? icon.cloneNode(true) : document.createTextNode(fallbackText);
  }

  /** The text column of a horizontal option card (Kyle, 2026-07-29): image on
   * the left, this to its right. It exists so the caption and the R9/R27
   * unavailable note stack as LINES beside the image instead of becoming two
   * more items in the card's flex row. It is a `<span>`, not a `<div>`, because
   * it lives inside the `<label>` that IS the click target — one target, one
   * tab stop, unchanged. */
  function stackedText(...children) {
    const box = document.createElement('span');
    box.className = 'eg-swatch__text';
    box.append(...children);
    return box;
  }

  /** R8/R26 — every section beyond the first of its kind carries a remove
   * control, and removing one NAMES where focus goes next. */
  function removeButton(section) {
    const button = document.createElement('button');
    button.type = 'button';
    // The id doubles as the morph key, so the button survives renders inside
    // the section footer it gets reparented into.
    button.id = `eg-remove-${section.id}`;
    button.className = 'eg-btn eg-btn--quiet eg-configurator__remove';
    button.textContent = 'Remove';
    button.setAttribute('aria-label', `Remove your ${ordinalWord(section.ordinal)} ${NOUN_FOR_KIND[section.kind]}`);
    const sectionId = section.id;
    const kind = section.kind;
    button.addEventListener('click', () => {
      // Ordinal derived AT CLICK TIME (morph audit, 2026-07-30): this node can
      // outlive many renders, and `ordinal` is positional — a captured copy
      // goes stale the moment an earlier same-kind section is removed. The id
      // and kind it closes over are stable for the section's lifetime.
      const ordinal = configurator
        .state()
        .sections.filter((candidate) => candidate.kind === kind)
        .findIndex((candidate) => candidate.id === sectionId);
      const next = configurator.removeSection(sectionId);
      apply(next);
      if (next.pendingBreak) return;
      focusAfterRemoval(kind, Math.max(ordinal, 0));
    });
    return button;
  }

  /** R26 — the next remaining section's first control, or the "add another"
   * button when none remain. NEVER `<body>`, which escapes the focus trap. */
  function focusAfterRemoval(kind, ordinal) {
    const slot = groups[kind];
    if (!slot?.list) return;
    const remaining = slot.list.querySelectorAll('[data-eg-section]');
    const target = remaining[Math.min(ordinal, remaining.length - 1)] ?? null;
    const control = target ? target.querySelector('input:not([disabled]), button:not([disabled])') : null;
    if (control) control.focus();
    else if (slot.add) slot.add.focus();
  }

  function sectionShell(section, selectedVariantId, stepLabel, { legend = true } = {}) {
    const fieldset = document.createElement('fieldset');
    fieldset.className = 'eg-configurator__section';
    fieldset.dataset.egSection = section.id;
    // The scroll-spy's handles (R5): what this section IS and what it currently
    // shows, so scrolling previews the section's own selection.
    fieldset.dataset.egStepKind = section.kind;
    fieldset.dataset.egStepLabel = stepLabel;
    if (selectedVariantId) fieldset.dataset.egStepVariant = selectedVariantId;

    // Design pass (2026-07-29, task 6) — Classics drops this legend entirely
    // ("Your first Classic"); Cover keeps it. `legend: false` is the only way
    // in, so a caller has to say so on purpose rather than a class or a kind
    // check inside this shared shell deciding it silently.
    if (legend) {
      const legendEl = document.createElement('legend');
      legendEl.className = 'eg-label';
      legendEl.textContent = `Your ${ordinalWord(section.ordinal)} ${NOUN_FOR_KIND[section.kind]}`;
      fieldset.appendChild(legendEl);
    }
    return fieldset;
  }

  /** R9 — one selection spans EVERY cover product, plus a "none" cell (R8).
   *
   * The colorways are grouped per cover product under a subheading, and each
   * group is a NESTED `<fieldset>` whose `<legend>` is that subheading: the step
   * is already a fieldset, so a group of groups is the shape that already fits,
   * and a legend is announced as the group's name without inventing a heading
   * level inside a form.
   *
   * KEYBOARD TRAVERSAL SURVIVES THE SPLIT because a radio group is defined by
   * shared `name` + form ownership, NOT by DOM containment: `name` is computed
   * once, above the loop, and every cell in every group still gets it. Arrow
   * keys therefore roll through all colorways across all groups as one roving
   * tabstop, exactly as they did when the list was flat.
   *
   * "No cover" is appended BEFORE the groups and outside them all: it is the
   * step's own "none" choice, not a colorway of any product (R8).
   */
  /** U3 (2026-10-06) — the second waitlisted group's one line. It points at
   * the box rather than growing a second one, because the widget is one live
   * node (D5) and two forms for one list is two places to typo an address. */
  const WAITLIST_ELSEWHERE = 'The waitlist is in your first cover above.';

  function renderCoverSection(section, claimWaitlist = () => false) {
    const chosen = section.options.find((option) => option.selected) ?? null;
    // Design pass (2026-07-29, batch 3) drops this section's own "Your first
    // cover" legend entirely (`sectionShell(..., { legend: false })`), same
    // treatment task 6 already gave Classics ("Your first Classic") — the
    // section-group's own `<h3>Cover</h3>` already names it.
    const fieldset = sectionShell(
      section,
      chosen?.variantId ?? null,
      chosen ? `${chosen.coverTitle} — ${chosen.title}` : 'Cover',
      { legend: false }
    );
    const name = `eg-cover-${section.id}`;

    fieldset.appendChild(
      radioCell(
        {
          id: `${name}-none`,
          name,
          checked: section.touched === true && section.coverVariantId === null,
          disabled: false,
          // Plain `__swatch`: `__swatch--none` was a modifier no rule has ever
          // mentioned, marking a distinction the design does not draw — the
          // Classics and Annual "none" cells carry no modifier either, and all
          // three are meant to read as one more card in their list.
          className: 'eg-swatch eg-swatch--none',
          onChange: () => apply(configurator.selectCover(section.id, null)),
          onFocus: () => focusStep('cover', null, 'Cover'),
        },
        stackedText(
          textSpan('eg-body eg-swatch__name', 'No cover'),
          textSpan('eg-caption eg-swatch__price', 'build without a cover')
        )
      )
    );

    // Design pass (2026-07-29) SUPERSEDES the flex-wrap/no-heading pass this
    // comment used to describe (Task 1/2, same date): subheadings are back,
    // one NESTED `<fieldset>` per cover product exactly as the function
    // docstring above always specified, each with a visible `<legend>` (the
    // subheading) and a FOUR-COLUMN grid of that product's colorway cards.
    // `ariaLabel` stays on every radio too — belt and suspenders now that the
    // heading is visible again, since a browser's accessible-name computation
    // prefers a control's own `<label>` over an ancestor `<legend>` and would
    // otherwise drop the product name from the name a screen reader announces.
    for (const group of groupCoverOptions(section.options)) {
      const groupField = document.createElement('fieldset');
      groupField.className = 'eg-configurator__cover-group';
      groupField.dataset.egKey = `cover-group-${group.title}`;

      const legend = document.createElement('legend');
      legend.className = 'eg-label eg-configurator__cover-group-legend';
      legend.textContent = group.title;
      groupField.appendChild(legend);

      // U3 / D4 (2026-10-06) — BETWEEN RUNS: every colorway of this product is
      // unbuyable, so there is no grid to build. The legend stays (it names the
      // product, from data, so the fieldset still says what the shopper is
      // waiting for) and a slot takes the grid's place — the legend's next
      // sibling either way, so it inherits the same `+ *` padding the grid had.
      //
      // The slot is EMPTY in the fresh tree: `renderSections` reparents the one
      // live widget into it after the morph, the same move the "Add another…"
      // buttons get. A SECOND waitlisted group (a shopper who added a second
      // Cover section, or a second product between runs) gets a note instead,
      // and the note rides IN the fresh tree, keyed — a post-morph write would
      // have to re-add it on every render and could not be seen by the diff.
      //
      // WHICH GROUP IS "FIRST" IS DECIDED AT RENDER TIME, by `claimWaitlist`
      // in `renderSections`, because the answer spans every cover section on
      // the rail and this function only ever sees one of them. Claim order is
      // render order is DOM order, which is why the `querySelector` over there
      // (first slot in DOM order) and the claim here agree.
      if (coverGroupWaitlisted(group)) {
        const waitlistSlot = document.createElement('div');
        waitlistSlot.className = 'eg-configurator__cover-waitlist-slot';
        waitlistSlot.dataset.egKey = 'cover-waitlist-slot';
        if (!claimWaitlist()) {
          const note = textSpan('eg-caption eg-swatch__note', WAITLIST_ELSEWHERE);
          note.dataset.egKey = 'cover-waitlist-note';
          waitlistSlot.appendChild(note);
        }
        groupField.appendChild(waitlistSlot);
        fieldset.appendChild(groupField);
        continue;
      }

      const grid = document.createElement('div');
      grid.className = 'eg-swatch-grid';

      for (const option of group.options) {
        // Design pass (2026-07-29, batch 3) — ``eg-swatch__name`/`eg-swatch__price`
        // carry the uniform 8px-above-name/8px-above-price/line-height:1
        // rhythm every image card shares (component-eg-configurator.css);
        // `eg-body`/`eg-caption` still supply the type itself.
        const lines = [
          textSpan('eg-body eg-swatch__name', option.title),
          textSpan('eg-caption eg-swatch__price', money(option.price)),
        ];
        // R9/R27 — greyed, unclickable, and LABELLED with the reason. In the
        // card the reason belongs in the text column, under the price, so it
        // stays beside its own image and inside its own label.
        if (!option.selectable) lines.push(textSpan('eg-caption eg-swatch__note', unavailableLabel(option)));
        grid.appendChild(
          radioCell(
            {
              id: `${name}-${option.variantId}`,
              // Shared across every group — one radio group, one roving tabstop.
              name,
              checked: option.selected,
              // A hydrated selection stays operable even when it is over the
              // limit — reflection always wins (R27).
              disabled: !option.selectable && !option.selected,
              className: 'eg-swatch',
              ariaLabel: `${option.coverTitle} — ${option.title}`,
              onChange: () => apply(configurator.selectCover(section.id, option.variantId)),
              onFocus: () => focusStep('cover', option.variantId, `${option.coverTitle} — ${option.title}`),
            },
            imageNode(option.image, option.title, 'eg-swatch__image'),
            stackedText(...lines)
          )
        );
      }

      groupField.appendChild(grid);
      fieldset.appendChild(groupField);
    }

    if (section.removable) fieldset.appendChild(removeButton(section));
    return fieldset;
  }

  /** R10 — a subscription checkbox that is present from the moment the
   * section renders and pre-checked. Card prices FOLLOW THE CHECKBOX, never
   * the selection, so the displayed price never moves on click (state's
   * `cadenceOptionsFor` already resolves that).
   *
   * Design pass (2026-07-29, task 6) drops this section's own "Your first
   * Classic" legend entirely (`sectionShell(..., { legend: false })`) and
   * pulls "No booklets" out of the priced grid into its own full-width
   * option, matching Cover's "No cover" cell — see below. */
  function renderClassicSection(section) {
    const chosen = section.options.find((option) => option.selected) ?? null;
    const fieldset = sectionShell(section, chosen?.variantId ?? null, chosen ? chosen.label : 'Classics', { legend: false });
    const name = `eg-cadence-${section.id}`;

    fieldset.appendChild(
      radioCell(
        {
          id: `${name}-none`,
          name,
          checked: section.touched === true && section.cadence === null,
          disabled: false,
          className: 'eg-swatch eg-swatch--none',
          onChange: () => apply(configurator.setCadence(section.id, null)),
          onFocus: () => focusStep('classic', null, 'Classics'),
        },
        stackedText(
          textSpan('eg-body eg-swatch__name', 'No booklets'),
          textSpan('eg-caption eg-swatch__price', 'build without the Classic')
        )
      )
    );

    /** R10/R24 — the four PRICED cadence cells are a FOUR-COLUMN grid
     * (design pass 2026-07-29 — "No booklets" moved out above, so this grid
     * is exactly the priced options, matching Cover's four-column card
     * grid). The subscription row and the remove control stay OUTSIDE it:
     * they are not choices in the same set, and inside the grid they would
     * take a cell each. */
    const cadences = document.createElement('div');
    cadences.className = 'eg-swatch-grid';
    cadences.dataset.egKey = 'cadences';

    for (const option of section.options) {
      // Design pass (2026-07-29, batch 3) — same ``eg-swatch__name`/`eg-swatch__price`
      // rhythm the colorway cards use, so the two four-column card grids
      // match; `__cadence-price` stays too, for its own accent colour.
      const children = [
        textSpan('eg-body eg-swatch__name', option.label),
        // Kyle, 2026-07-29 — the SAME classes the Heirloom cards' price uses:
        // `__cadence-price` (its own accent colour) is dropped so there is one
        // price treatment across every card in the rail.
        textSpan('eg-caption eg-swatch__price', money(option.price)),
        textSpan('eg-caption', option.note),
      ];
      if (option.disabled) children.push(textSpan('eg-caption eg-swatch__note', unavailableLabel(option)));
      cadences.appendChild(
        radioCell(
          {
            id: `${name}-${option.booklets}`,
            name,
            checked: option.selected,
            disabled: option.disabled && !option.selected,
            // Kyle, 2026-07-29 — cadence cards carry their PHOTO now, exactly
            // like the cover and sleeve cards: `--card` supplies the shared
            // image-on-top card composition, so the two grids read as one
            // language. The photo per cadence has always been in the id map
            // (`image`, from the cadence variant, falling back to the Classic
            // product's featured image) and travelled all the way to this
            // option object — only this renderer never drew it.
            className: 'eg-swatch',
            onChange: () => apply(configurator.setCadence(section.id, option.booklets)),
            onFocus: () => focusStep('classic', option.variantId, option.label),
          },
          imageNode(option.image, option.label, 'eg-swatch__image'),
          stackedText(...children)
        )
      );
    }

    fieldset.appendChild(cadences);

    // R10 — visible from the first render, pre-checked, naming the cadence and
    // the saving. It keeps its state through "No booklets"; it simply then
    // governs no cart line.
    const subscribeId = `eg-subscribe-${section.id}`;
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.id = subscribeId;
    checkbox.className = 'eg-configurator__checkbox';
    checkbox.checked = section.subscribe;
    checkbox.addEventListener('change', (event) => apply(configurator.setSubscribe(section.id, event.target.checked)));

    // R10 — the checkbox NAMES THE CADENCE in its label (this was specified
    // and the string had been hardcoded to "quarterly"): the period follows
    // the selected cadence, and the saving note repeats per that period.
    const SUBSCRIBE_PERIOD = {
      1: { adjective: 'monthly', every: 'every month' },
      3: { adjective: 'quarterly', every: 'every quarter' },
      6: { adjective: 'six-month', every: 'every six months' },
      12: { adjective: 'full-year', every: 'every year' },
    };
    const savingSource = chosen ?? section.options[0] ?? null;
    const period = SUBSCRIBE_PERIOD[savingSource?.booklets] ?? SUBSCRIBE_PERIOD[3];

    const subscribeLabel = document.createElement('label');
    subscribeLabel.className = 'eg-configurator__subscribe';
    subscribeLabel.setAttribute('for', subscribeId);
    subscribeLabel.append(textSpan('eg-body-s', `Make this a ${period.adjective} subscription`));

    if (savingSource && savingSource.saving > 0) {
      subscribeLabel.append(textSpan('eg-caption', `save ${money(savingSource.saving)} ${period.every}`));
    }

    const subscribeRow = document.createElement('div');
    subscribeRow.className = 'eg-configurator__subscribe-row';
    subscribeRow.dataset.egKey = `subscribe-${section.id}`;
    subscribeRow.dataset.egSubscribe = section.id;
    subscribeRow.append(checkbox, subscribeLabel);
    // Kyle, 2026-07-29 (supersedes R10's always-visible rule): the checkbox
    // only shows once booklets are actually chosen — with "No booklets" it
    // governs nothing and just takes up room. `hidden`, not skipped, so its
    // R10 state survives the round trip and re-appears exactly as it was.
    subscribeRow.hidden = section.cadence === null;
    fieldset.appendChild(subscribeRow);

    if (section.removable) fieldset.appendChild(removeButton(section));
    return fieldset;
  }

  /**
   * R11 REDESIGNED (design pass 2026-07-29, task 3) — ONE row, styled
   * exactly like an Accessories row (image, title/price, a quantity
   * stepper), plus its own "No annual" full-width option — the same visual
   * language as Cover's "No cover" and Classics' "No booklets" above.
   * "Add another annual" is gone completely; the stepper is the only way to
   * grow or shrink this any more.
   *
   * THE STATE MODEL DOES NOT CHANGE: quantity is still N annual-kind
   * SECTIONS (R8/R11), each independently carrying `present`/`fromKit`/its
   * own id — this view only stops drawing one fieldset per section and
   * draws the COUNT of present ones instead. `incrementAnnual`/
   * `decrementAnnual`/`clearAnnual` below drive that count through the
   * EXACT SAME action paths every other section already uses:
   *
   *   `+` on a DORMANT section (one already in state, `present: false`) —
   *       `configurator.setAnnual(id, true)`, the same call the old "One
   *       annual" radio made.
   *   `+` with none dormant — `configurator.addSection('annual')`, the SAME
   *       call "Add another annual" used to make, immediately followed by
   *       `setAnnual` on the section it just inserted (a fresh section is
   *       never `fromKit`, so this leg never raises the Kit-break confirm).
   *   `−` — `configurator.removeSection(id)` for any section beyond the
   *       first of its kind, or `configurator.setAnnual(id, false)` for the
   *       first (R8: the first of a kind has no remove control) — the SAME
   *       two calls the old per-instance radio and remove button made.
   *
   * Both `−` paths already run through `commitOrRaiseBreak` (R19), so a
   * `fromKit` annual still raises the Kit-break confirm exactly as before;
   * `apply()` (unchanged) renders whatever comes back, including a
   * `pendingBreak`, so a decline simply re-renders the count from state —
   * the stepper keeps no count of its own to fall out of sync with it.
   */
  function annualSections() {
    return configurator.state().sections.filter((section) => section.kind === 'annual');
  }

  function incrementAnnual() {
    const dormant = annualSections().find((section) => !section.present);
    if (dormant) {
      apply(configurator.setAnnual(dormant.id, true));
      return;
    }
    configurator.addSection('annual');
    const grown = annualSections();
    const added = grown[grown.length - 1] ?? null;
    apply(added ? configurator.setAnnual(added.id, true) : configurator.state());
  }

  function decrementAnnual() {
    const sections = annualSections();
    const present = sections.filter((section) => section.present);
    const last = present[present.length - 1] ?? null;
    if (!last) return;
    const isFirst = sections[0]?.id === last.id;
    apply(isFirst ? configurator.setAnnual(last.id, false) : configurator.removeSection(last.id));
  }

  /** The "No annual" radio — reduces the present count to zero, one
   * existing action at a time rather than a bespoke bulk remove: a
   * Kit-break confirm raised mid-reduction stops the loop in exactly the
   * place a single `−` press already stops (R19), and `apply()` shows it;
   * the next click — on the confirm, or on the radio again — continues it. */
  function clearAnnual() {
    let next = configurator.state();
    // NOTHING PRESENT IS STILL A CHOICE (Kyle, 2026-07-29). The loop below
    // only ever unwinds sections that ARE present, so on a fresh section it
    // broke immediately, no setter ran, and "No annual" could never register
    // as chosen — the same shape of bug the cover/classics none-cells had.
    // One explicit `setAnnual(..., false)` marks it; state treats that as a
    // real choice on an untouched section and as a no-op afterwards.
    const annualsNow = next.sections.filter((section) => section.kind === 'annual');
    if (annualsNow.length > 0 && !annualsNow.some((section) => section.present)) {
      apply(configurator.setAnnual(annualsNow[0].id, false));
      return;
    }
    for (;;) {
      const sections = next.sections.filter((section) => section.kind === 'annual');
      const present = sections.filter((section) => section.present);
      const last = present[present.length - 1] ?? null;
      if (!last) break;
      const isFirst = sections[0]?.id === last.id;
      next = isFirst ? configurator.setAnnual(last.id, false) : configurator.removeSection(last.id);
      if (next.pendingBreak) break;
    }
    apply(next);
  }

  function renderAnnualSection(state) {
    const sections = state.sections.filter((section) => section.kind === 'annual');
    const first = sections[0];
    const count = sections.filter((section) => section.present).length;
    const title = first.title || 'Annual';
    const hasAnnual = idMap.annual != null;
    const limit = first.limit;
    const canIncrease = hasAnnual && (limit === null || count < limit);

    const fieldset = document.createElement('fieldset');
    fieldset.className = 'eg-configurator__section';
    fieldset.dataset.egKey = 'annual';
    fieldset.dataset.egStepKind = 'annual';
    fieldset.dataset.egStepLabel = title;
    if (count > 0 && idMap.annual) fieldset.dataset.egStepVariant = idMap.annual.variantId;

    fieldset.appendChild(
      radioCell(
        {
          id: 'eg-annual-none',
          name: 'eg-annual',
          // Nothing pre-selected on a fresh load (Kyle, 2026-07-29): zero
          // annuals only reads as "No annual" once a section has been touched.
          checked: count === 0 && sections.some((section) => section.touched === true),
          disabled: false,
          className: 'eg-swatch eg-swatch--none',
          onChange: () => clearAnnual(),
          onFocus: () => focusStep('annual', null, title),
        },
        stackedText(
          textSpan('eg-body eg-swatch__name', 'No annual'),
          textSpan('eg-caption eg-swatch__price', 'build without the Annual')
        )
      )
    );

    // Styled exactly like an Accessories CARD (Kyle, design pass 2026-07-30 —
    // was a horizontal row): image on top, name, price, stepper at the foot.
    // One column of the shared 4-column swatch grid keeps it the same width
    // as every other card in the rail.
    const row = document.createElement('div');
    // `--picked` at 1+ (Kyle, 2026-07-30) — a quantity card holding one or more
    // frames like any other chosen option; zero frames unselected.
    row.className = count > 0 ? 'eg-swatch eg-swatch--picked' : 'eg-swatch';
    row.appendChild(imageNode(first.image, title, 'eg-swatch__image'));
    row.appendChild(textSpan('eg-body eg-swatch__name', title));
    row.appendChild(textSpan('eg-caption eg-swatch__price', money(first.price)));
    if (!canIncrease) {
      const note = textSpan(
        'eg-caption eg-swatch__note',
        unavailableLabel({ reason: !hasAnnual ? 'unavailable' : limit === 0 ? 'sold-out' : 'claimed', limit })
      );
      // Keyed for the same reason as the accessory note (adversarial review,
      // 2026-07-30): its coming and going must not shift the stepper's
      // positional identity.
      note.dataset.egKey = 'annual-note';
      row.appendChild(note);
    }

    const stepper = document.createElement('div');
    stepper.className = 'eg-swatch__qty';
    stepper.dataset.egKey = 'annual-stepper';

    const minus = document.createElement('button');
    minus.type = 'button';
    minus.id = 'eg-annual-minus';
    minus.className = 'eg-swatch__qty-btn';
    minus.appendChild(qtyGlyph('minus', '−'));
    minus.setAttribute('aria-label', `Remove one ${title}`);
    minus.disabled = count === 0;
    minus.addEventListener('click', () => decrementAnnual());
    minus.addEventListener('focus', () => focusStep('annual', idMap.annual?.variantId ?? null, title));

    const countEl = textSpan('eg-swatch__qty-count eg-body-s', String(count));
    countEl.setAttribute('aria-label', `Quantity of ${title}`);

    const plus = document.createElement('button');
    plus.type = 'button';
    plus.id = 'eg-annual-plus';
    plus.className = 'eg-swatch__qty-btn';
    plus.appendChild(qtyGlyph('plus', '+'));
    plus.setAttribute('aria-label', `Add one ${title}`);
    plus.disabled = !canIncrease;
    plus.addEventListener('click', () => incrementAnnual());
    plus.addEventListener('focus', () => focusStep('annual', idMap.annual?.variantId ?? null, title));

    stepper.append(minus, countEl, plus);
    row.appendChild(stepper);

    const grid = document.createElement('div');
    grid.className = 'eg-swatch-grid';
    grid.dataset.egKey = 'annual-grid';
    row.dataset.egKey = 'annual-card';
    grid.appendChild(row);
    fieldset.appendChild(grid);

    return fieldset;
  }

  const RENDERERS = { cover: renderCoverSection, classic: renderClassicSection };

  function renderSections(state) {
    for (const kind of Object.keys(GROUP_FOR_KIND)) {
      const slot = groups[kind];
      if (!slot?.list) continue;

      // Design pass (2026-07-29, task 3) — Annual collapsed to ONE row with
      // a quantity stepper; it no longer repeats a fieldset per instance, so
      // it takes its own path here and never reaches the "Add another …"
      // reparenting or the add-button binding below (both scoped to the two
      // kinds that still repeat, and Annual's own "Add another" button no
      // longer exists in the markup for the binding to find anyway).
      if (kind === 'annual') {
        morphChildren(slot.list, [renderAnnualSection(state)]);
        continue;
      }

      // U3 (2026-10-06) — one claim per render pass, across EVERY section of
      // this kind: the first waitlisted cover group to ask gets the live
      // widget, the rest get the note. `renderClassicSection` ignores the
      // argument; only Cover has a waitlist.
      let waitlistClaimed = false;
      const claimWaitlist = () => (waitlistClaimed ? false : (waitlistClaimed = true));

      const fieldsets = state.sections
        .filter((section) => section.kind === kind)
        .map((section) => RENDERERS[kind](section, claimWaitlist));
      const children = [...fieldsets];

      // Task 3 (Kyle, 2026-07-29) — REMOVE ALIGNMENT: the remove control
      // nearest the "Add another …" button sits in the SAME horizontal row
      // as it, rather than stranded at the bottom of its own card. Scoped to
      // covers and classics only, per the design brief.
      //
      // Morph rework (2026-07-30): the footer is built INTO the fresh tree
      // (the remove button is plucked out of the still-detached last
      // fieldset), so the diff sees one consistent shape and can keep the
      // live footer + remove button across renders. The static "Add
      // another…" button is NOT part of the fresh tree — it is a live
      // singleton the Liquid marks `data-eg-keep` (invisible to the diff);
      // the guarded append after the morph parents it into the live footer
      // exactly once, and every render after that is a no-op.
      const wantsFooter = (kind === 'cover' || kind === 'classic') && slot.add;
      if (wantsFooter) {
        const lastFieldset = fieldsets[fieldsets.length - 1] ?? null;
        const lastRemove = lastFieldset ? lastFieldset.querySelector('.eg-configurator__remove') : null;
        const footer = document.createElement('div');
        footer.className = 'eg-configurator__section-footer';
        footer.dataset.egKey = 'section-footer';
        if (lastRemove) footer.appendChild(lastRemove);
        children.push(footer);
      }

      morphChildren(slot.list, children);

      if (wantsFooter) {
        const liveFooter = slot.list.querySelector(':scope > [data-eg-key="section-footer"]');
        if (liveFooter && slot.add.parentElement !== liveFooter) liveFooter.appendChild(slot.add);
      }

      // U3 (2026-10-06) — the waitlist widget's one and only move: REPARENT.
      // `querySelector` returns the first slot in DOM order, which is the one
      // `claimWaitlist` left empty. The widget is never rebuilt and never
      // morphed (it carries `data-eg-keep`), so a half-typed address and the
      // thank-you panel eg-capture.js swapped in both survive every render.
      // No slot means no cover product is between runs — the colorways came
      // back, or the shopper deleted the section — so it goes home and hides
      // rather than lingering under a grid it has nothing to say about. Home
      // is the section-group it was authored in; it lands at the end of that
      // group rather than at its original index, which costs nothing because
      // it is hidden the whole time it is there.
      if (kind === 'cover' && el.coverWaitlist) {
        const liveSlot = slot.list.querySelector('[data-eg-key="cover-waitlist-slot"]');
        const host = liveSlot ?? coverWaitlistHome;
        if (host && el.coverWaitlist.parentElement !== host) host.appendChild(el.coverWaitlist);
        el.coverWaitlist.hidden = !liveSlot;
      }

      if (slot.add && !slot.add.dataset.egBound) {
        slot.add.dataset.egBound = 'true';
        slot.add.addEventListener('click', () => {
          apply(configurator.addSection(kind));
          // R26 — the new section is where the shopper is now.
          const added = slot.list.querySelectorAll('[data-eg-section]');
          added[added.length - 1]?.querySelector('input:not([disabled])')?.focus();
        });
      }
    }
  }

  /** R12 — the one multi-select section, +/− steppers, unhidden only when the
   * id map's accessories array is non-empty (the map emits in-stock variants
   * only, so stock returning brings the section back with no deploy). */
  function renderAccessories(state) {
    if (el.accessoriesGroup) {
      el.accessoriesGroup.hidden = !state.hasAccessories;
      // Kyle, 2026-07-29 — ONE step for the whole section: scrolling through
      // accessory rows never flips the stage (the per-row markers that did
      // that are gone). No step-variant here, so the hero shows the first
      // accessory — or the shopper's persisted circle pick (stepFromScroll).
      el.accessoriesGroup.dataset.egStepKind = 'accessory';
      el.accessoriesGroup.dataset.egStepLabel = 'Accessories';
    }
    if (!el.accessories || !state.hasAccessories) return;

    morphChildren(
      el.accessories,
      // Same rule as `radioCell` above: an accessory that cannot be bought is
      // not drawn. `quantity > 0` keeps one the shopper already holds.
      state.accessories
        .filter((accessory) => accessory.selectable || accessory.quantity > 0)
        .map((accessory) => {
        // Kyle, design pass 2026-07-30 — a top-down CARD like every other
        // image swatch in the rail (was a horizontal row): image on top, then
        // name (`eg-swatch__name`), price (`eg-swatch__price`, Inconsolata via
        // `eg-caption`), stepper at the foot. Same classes as the cover and
        // cadence cards so all of them answer to the same dials.
        const row = document.createElement('div');
        // `--picked` at 1+ (Kyle, 2026-07-30) — see the Annual card above.
        row.className = accessory.quantity > 0 ? 'eg-swatch eg-swatch--picked' : 'eg-swatch';
        row.dataset.egKey = `accessory-${accessory.variantId}`;

        row.appendChild(imageNode(accessory.image, accessory.title, 'eg-swatch__image'));
        row.appendChild(textSpan('eg-body eg-swatch__name', accessory.title));
        row.appendChild(textSpan('eg-caption eg-swatch__price', money(accessory.price)));
        if (!accessory.selectable) {
          const note = textSpan('eg-caption eg-swatch__note', unavailableLabel(accessory));
          // Keyed so the note's coming and going never shifts the stepper's
          // positional identity (adversarial review, 2026-07-30).
          note.dataset.egKey = `accessory-note-${accessory.variantId}`;
          row.appendChild(note);
        }

        const stepper = document.createElement('div');
        stepper.className = 'eg-swatch__qty';
        stepper.dataset.egKey = `accessory-stepper-${accessory.variantId}`;

        const minus = document.createElement('button');
        minus.type = 'button';
        minus.id = `eg-accessory-minus-${accessory.variantId}`;
        minus.className = 'eg-swatch__qty-btn';
        minus.appendChild(qtyGlyph('minus', '−'));
        minus.setAttribute('aria-label', `Remove one ${accessory.title}`);
        minus.disabled = !accessory.canDecrease;
        minus.addEventListener('click', () => apply(configurator.stepAccessory(accessory.variantId, -1)));
        minus.addEventListener('focus', () => focusStep('accessory', accessory.variantId, accessory.title));

        const count = textSpan('eg-swatch__qty-count eg-body-s', String(accessory.quantity));
        count.setAttribute('aria-label', `Quantity of ${accessory.title}`);

        const plus = document.createElement('button');
        plus.type = 'button';
        plus.id = `eg-accessory-plus-${accessory.variantId}`;
        plus.className = 'eg-swatch__qty-btn';
        plus.appendChild(qtyGlyph('plus', '+'));
        plus.setAttribute('aria-label', `Add one ${accessory.title}`);
        // R27 — a stepper is the same claim in a different control, so it caps
        // at the same number a picker does.
        plus.disabled = !accessory.canIncrease;
        plus.addEventListener('click', () => apply(configurator.stepAccessory(accessory.variantId, 1)));
        plus.addEventListener('focus', () => focusStep('accessory', accessory.variantId, accessory.title));

        stepper.append(minus, count, plus);
        row.appendChild(stepper);
        return row;
      })
    );
  }

  /** R28 — the itemized breakdown lists EXACTLY the lines the footer total
   * sums: the scoped lines this surface would commit, plus the sealed Kit line
   * that is already in the cart. Unscoped cart items appear nowhere (R14). */
  function renderBreakdown(state) {
    const rows = [...state.lines];
    if (state.kitLine) rows.push(state.kitLine);
    // EMPTY BUILD = HIDDEN, restored (Kyle, 2026-07-30 — "fix this random
    // border"). The constant-height empty state that replaced hiding on
    // 2026-07-29 existed ONLY to mask the bottom-of-scroll jitter by pinning
    // scrollHeight — and with nothing itemized it painted a stray hairline
    // over 160px of dead space at the rail's foot. The jitter's real causes
    // are gone (the morph keeps nodes and focus alive, so a render can no
    // longer yank the scroll), so the group simply hides again until there
    // is something to itemize. If bottom jitter ever returns, this toggle is
    // where the old mask lived — read that commit before re-masking.
    const empty = rows.length === 0;
    if (el.breakdownGroup) el.breakdownGroup.hidden = empty;
    if (!el.breakdownLines || empty) return;
    morphChildren(
      el.breakdownLines,
      rows.map((line, index) => {
        const li = document.createElement('li');
        li.className = 'eg-configurator__breakdown-line';
        // variantId + subscription flag identifies a line; the index breaks
        // the tie when the same variant appears on two lines.
        li.dataset.egKey = `line-${line.variantId ?? line.title}-${line.subscription ? 'sub' : 'one'}-${index}`;
        const name = line.detail ? `${line.title} — ${line.detail}` : line.title;
        li.appendChild(textSpan('eg-body-s', line.quantity > 1 ? `${name} × ${line.quantity}` : name));
        if (line.subscription) li.appendChild(textSpan('eg-caption eg-configurator__badge', 'subscription'));
        li.appendChild(textSpan('eg-body-s', money(line.linePrice)));
        return li;
      })
    );
  }

  /**
   * KTD-D's READ-ONLY REGISTER, given a surface (U9).
   *
   * `hydrate()` captures scoped lines this surface must never touch — a selling
   * plan on a cover, Annual or accessory, or a Classics plan that is not the
   * map's plan for that cadence — because the section model has nowhere to hold
   * that plan and editing it could only destroy information. KTD-D says those
   * lines are "DISPLAYED"; nothing implemented that, so until now such a shopper
   * saw the line NOWHERE and the configurator's total silently disagreed with
   * their own cart with no explanation offered.
   *
   * It sits OUTSIDE the itemized breakdown on purpose (R28): that list must name
   * exactly the lines the footer total sums, and these are not among them. So it
   * is a separate, clearly-labelled note — quiet and factual, because this is an
   * edge case and not a headline.
   */
  function titleForVariant(variantId) {
    const entry = galleries.get(variantId) ?? null;
    for (const cover of idMap.covers) {
      for (const way of cover.colorways) {
        if (way.variantId === variantId) return `${cover.title} — ${way.title}`;
      }
    }
    for (const cadence of idMap.classic.cadences) {
      if (cadence.variantId === variantId) return `${idMap.classic.title} — ${cadence.label}`;
    }
    if (idMap.annual && idMap.annual.variantId === variantId) return idMap.annual.title;
    return entry?.title ?? 'An item in your cart';
  }

  function renderReadOnly(lines) {
    if (!el.readOnly) return;
    el.readOnly.hidden = lines.length === 0;
    if (lines.length === 0) return;
    if (el.readOnlyLines) {
      el.readOnlyLines.replaceChildren(
        ...lines.map((line) => {
          const li = document.createElement('li');
          li.className = 'eg-configurator__readonly-line';
          const name = titleForVariant(line.variantId);
          li.appendChild(textSpan('eg-body-s', line.quantity > 1 ? `${name} × ${line.quantity}` : name));
          return li;
        })
      );
    }
    if (el.readOnlyNote) {
      // `hydrate()` reports two reasons. Naming the subscription is the useful
      // thing to say in the case that actually happens; a line we could not
      // address at all gets the honest generic instead of a guess.
      const allPlans = lines.every((line) => line.reason === 'off-map-selling-plan');
      const one = lines.length === 1;
      const what = allPlans
        ? one
          ? 'This is on a subscription this builder can’t change'
          : 'These are on subscriptions this builder can’t change'
        : one
          ? 'This one isn’t something this builder can change'
          : 'These aren’t things this builder can change';
      el.readOnlyNote.textContent = one
        ? `${what}, so we’ve left it exactly as it is. It isn’t part of the total above.`
        : `${what}, so we’ve left them exactly as they are. They aren’t part of the total above.`;
    }
  }

  /**
   * R22 — bundle status is COUNTED and PHRASED, never priced. Two lines,
   * confirm then nudge, and no `−$5.00` row anywhere (KTD-H). The counting
   * itself is eg-bundle-nudge.js's, including the Kit+cover pairing that IS a
   * complete bundle rather than merely one cover off the trio.
   */
  function renderBundle(state) {
    const status = bundleStatus(state.lines, state.kitLine ? [state.kitLine] : []);
    // bundle_status_shown (2026-09-02, lower priority) — debounced to real
    // state changes only; renderBundle runs on every render pass.
    const bundleSignature = `${status.complete}:${status.missing.length}`;
    if (bundleSignature !== lastBundleSignature) {
      lastBundleSignature = bundleSignature;
      track('bundle_status_shown', { missing_count: status.missing.length, complete_count: status.complete });
    }
    if (el.bundleConfirm) {
      el.bundleConfirm.hidden = status.complete === 0;
      el.bundleConfirm.textContent = status.complete === 0 ? '' : 'Bundle discount applies at checkout';
    }
    if (el.nudge) {
      // #10 — the one composed sentence; announce() reads the same source.
      const text = nudgeSentence(status);
      el.nudge.hidden = text === '';
      el.nudge.textContent = text;
    }
    return status;
  }

  function renderFooter(state) {
    if (el.total) el.total.textContent = money(state.total);
    if (el.submit) el.submit.disabled = !state.canComplete;
    return renderBundle(state);
  }

  // ──────────────────────────────────────── U10: "Included at no cost" (R13)

  /** Every Classics subscription row currently on the surface, in document
   * order. U6 stamps `data-eg-subscribe="{sectionId}"` on each one; this is
   * the only handle the link needs, and reading it live (rather than caching)
   * is deliberate — sections are replaced wholesale on every render (R8). */
  function subscribeRows() {
    return el.sections ? [...el.sections.querySelectorAll('[data-eg-subscribe]')] : [];
  }

  /**
   * R13 — the read-only section, whose content follows the build. The verdict
   * is READ from `inclusionsFor()` (U3) and phrased by `inclusionsCopy` above;
   * nothing here decides anything. The Kit travels separately from `lines()`
   * because state excludes `fromKit` sections while the Kit line stands
   * (KTD-E/R18), which is exactly the shape `inclusionsFor` asks for and the
   * same pair `renderBundle` hands it.
   *
   * The link is hidden when there is no Classics subscription row to scroll
   * to. In practice there always is — the first section of each kind is not
   * removable — but a link that scrolls nowhere is worse than no link.
   */
  function renderInclusions(state) {
    const copy = inclusionsCopy(inclusionsFor(state.lines, state.kitLine ? [state.kitLine] : []));
    const hidden = copy.state === 'none';
    // Design pass (2026-07-29, batch 3) — hide the OUTER section-group too
    // (SELECTORS.inclusionsGroup), not just the inner content box below.
    // Leaving the outer wrapper visible-but-empty was the 160px-gap bug:
    // an empty box still collects its own 80px padding-top from the
    // section-group rhythm rule (component-eg-configurator.css), stacking
    // with the next visible group's own 80px.
    if (el.inclusionsGroup) el.inclusionsGroup.hidden = hidden;
    if (el.inclusions) el.inclusions.hidden = hidden;
    // Both lines come off the SAME `copy`, so the heading can never describe a
    // different state from the sentence under it. Only written when there is
    // something to say: the 'none' state hides the whole group, and blanking
    // the heading first would flash an empty <h3> on the way out.
    if (el.inclusionsHeading && copy.heading) el.inclusionsHeading.textContent = copy.heading;
    if (el.inclusionsText) el.inclusionsText.textContent = copy.text;
    // Both states carry a note now (Kyle, 2026-07-30 — it was trial-only when
    // it was a button). It hides only when there is nothing to say, which is
    // the 'none' state, which hides the whole group anyway. No focus rescue is
    // needed: a `<p>` holds no focus to lose, and the note points at nothing.
    if (el.inclusionsSubnote) {
      if (copy.note) el.inclusionsSubnote.textContent = copy.note;
      el.inclusionsSubnote.hidden = !copy.note;
    }
    return copy;
  }

  /** R25 — asked fresh every time, because a shopper can change the OS setting
   * with the modal open and the next press must honour it. */
  function prefersReducedMotion() {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * R13/R25/R26 — following "Make it ongoing": scroll to the Classics
   * subscription checkbox and highlight it.
   *
   * THREE THINGS HAPPEN, AND THE ORDER MATTERS.
   *
   * 1. FOCUS, with `preventScroll` (R26). Following this link by keyboard has
   *    to move focus somewhere useful, not just move the viewport — a scroll
   *    that strands focus on a now-off-screen link is the WCAG 2.4.3 failure
   *    this surface has already had to name twice. Focus lands on the CHECKBOX
   *    itself: the control the link is about, one Space away from doing the
   *    thing the link offered. `preventScroll` suppresses the browser's own
   *    instant jump so the one scroll the shopper sees is ours (step 3).
   * 2. THE HIGHLIGHT STATE. `.eg-configurator__pulse` (U7) carries BOTH the
   *    ring and the animation, and U7's `prefers-reduced-motion` block keeps
   *    the ring while dropping only the keyframes — so the state lands either
   *    way and following the link is never a silent no-op for a shopper who
   *    asked for less motion (R25, AE9). The remove/reflow/add dance restarts
   *    the animation on a second press; without it the second press would look
   *    broken. The class is not cleaned up on a timer on purpose: the next
   *    render replaces the sections wholesale, so ticking the box — or any
   *    other edit — clears it, and until then the ring is exactly the state a
   *    shopper needs to still be able to see.
   * 3. THE SCROLL, honouring reduced motion in its `behavior`. The stage is
   *    ALSO driven explicitly rather than left to the scroll-spy: a smooth
   *    scroll settles asynchronously, and AE9 requires the stage to have
   *    followed even on the instant path.
   *
   * Preference among rows: the first UNTICKED one, because that is the box
   * whose ticking would actually make the build ongoing. Falling back to the
   * first row covers the one case where the link shows with every box already
   * ticked — a Classics section set to "No booklets" keeps its checkbox state
   * (R10) but contributes no line, so nothing is subscribed yet.
   */
  function highlightSubscribe() {
    const rows = subscribeRows();
    if (rows.length === 0) return null;
    const target = rows.find((row) => row.querySelector('input[type="checkbox"]')?.checked === false) ?? rows[0];
    const checkbox = target.querySelector('input[type="checkbox"]');

    if (checkbox && typeof checkbox.focus === 'function') checkbox.focus({ preventScroll: true });

    // U7's CSS puts the ring on the checkbox's own label.
    const ring = target.querySelector('label') ?? target;
    ring.classList.remove('eg-configurator__pulse');
    void ring.offsetWidth;
    ring.classList.add('eg-configurator__pulse');

    const section = target.closest('[data-eg-step-kind]');
    if (section) {
      focusStep(
        section.dataset.egStepKind,
        section.dataset.egStepVariant || null,
        section.dataset.egStepLabel || ''
      );
    }

    if (typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
    }
    return target;
  }

  // (The "Make it ongoing" button that used to be wired to `highlightSubscribe`
  // here is gone — Kyle, 2026-07-30. The inclusion states the rule in a note
  // and offers no control, so there is nothing left to click.)

  /** One composed sentence per change (byob R5a): what is selected, how many
   * lines, where the bundle stands, and the BUILD's total (R28) — never a
   * number this surface did not compute. */
  function announce(state, status, inclusions) {
    const parts = [];
    for (const section of state.sections) {
      if (section.kind === 'cover') {
        const chosen = section.options.find((option) => option.selected);
        parts.push(chosen ? `${chosen.coverTitle} ${chosen.title} cover` : 'no cover');
      } else if (section.kind === 'classic') {
        const chosen = section.options.find((option) => option.selected);
        parts.push(chosen ? `${chosen.label}${section.subscribe ? ', subscribed' : ', one-off'}` : 'no booklets');
      } else {
        parts.push(section.present ? 'one Annual' : 'no Annual');
      }
    }
    for (const accessory of state.accessories) {
      if (accessory.quantity > 0) parts.push(`${accessory.quantity} ${accessory.title}`);
    }
    // D5 — COUNT WHAT THE TOTAL SUMS. `state.lines` excludes the sealed Kit
    // line while the Kit stands (KTD-E/R18), but `state.total` includes it and
    // so does the itemized breakdown — so a Kit-only build announced "… 0
    // items. … build total $47.00.", two halves of one sentence contradicting
    // each other. Same rows `renderBreakdown` draws, counted the same way.
    const itemCount = state.lines.length + (state.kitLine ? 1 : 0);
    parts.push(`${itemCount} item${itemCount === 1 ? '' : 's'}`);
    if (status.complete > 0) parts.push('bundle discount applies at checkout');
    // #10 — the SAME sentence the visible nudge shows (lower-cased into the
    // running announcement, trailing stop dropped), never a second phrasing.
    const nudge = nudgeSentence(status);
    if (nudge !== '') parts.push(nudge.charAt(0).toLowerCase() + nudge.slice(1, -1));
    // R13/R26 — the inclusions section is read-only, so its content changing is
    // the one thing on this surface a screen-reader shopper would otherwise
    // have no way to notice. Same sentence they'd read, no second phrasing.
    if (inclusions && inclusions.text !== '') parts.push(`included at no cost, ${inclusions.text.replace(/\.$/, '')}`);
    parts.push(`build total ${money(state.total)}`);
    if (el.announce) el.announce.textContent = `${parts.join('. ')}.`;
  }

  function render(state) {
    // The confirmation replaces the build view wholesale (R23); repainting
    // controls underneath it would be work nobody can see.
    if (surface === 'confirmation') return;
    let status;
    let inclusions;
    withFocusKept(() => {
      renderSections(state);
      renderAccessories(state);
      renderBreakdown(state);
      status = renderFooter(state);
      // After the sections, because the link hides itself when there is no
      // subscription row to scroll to (R13).
      inclusions = renderInclusions(state);
    });
    renderStage();
    // After the paint: adding or removing a section changes whether the column
    // has anything left below the fold, and no scroll event fires for that.
    syncFooterEdge();
    announce(state, status, inclusions);
  }

  // ──────────────────────────────────────────────── D4: the real focus trap

  /** Anything the browser might make a tab stop. Filtered properly below —
   * this is only the cheap first pass. */
  const TABBABLE = 'a[href], button, input, select, textarea, summary, [tabindex]';

  /** The one surface that is up, and therefore the only thing focus may visit.
   * The build view is `inert` while an overlay is raised (R26) and the
   * confirmation REPLACES it outright (R23), so exactly one of these is live. */
  function liveSurfaceRoot() {
    if (OVERLAY_SURFACES.includes(surface)) return el.confirm;
    if (surface === 'confirmation') return el.viewConfirmation;
    return el.viewBuild;
  }

  /**
   * The live surface's tab stops, in document order.
   *
   * The visually-hidden radios and checkboxes MUST stay in this list: they are
   * the real controls (the label is only their paint), and dropping them would
   * make the trap skip every choice on the surface. They are 1px and clipped,
   * not `display: none`, so they still report client rects — which is exactly
   * the test that separates them from a control inside a `[hidden]` block.
   */
  function tabStops() {
    const scope = liveSurfaceRoot();
    if (!scope) return [];
    return [...scope.querySelectorAll(TABBABLE)].filter((node) => {
      if (node.disabled === true) return false;
      if (Number(node.tabIndex) < 0) return false;
      if (node.closest('[hidden], [inert]') !== null) return false;
      if (node.getClientRects().length === 0) return false;
      // A radio group is ONE tab stop, and it is the checked member (the
      // browser's roving tabstop). Counting the others would have the trap
      // hand out stops the browser never visits, and the wrap would fire in
      // the wrong place.
      if (node.type === 'radio' && node.checked !== true && node.name) {
        const peers = scope.querySelectorAll(`input[type="radio"][name="${CSS.escape(node.name)}"]`);
        if ([...peers].some((peer) => peer.checked)) return false;
      }
      return true;
    });
  }

  /** R26/D4 — Tab may never leave the live surface. The decision itself is
   * `tabWrapTarget` (pure, unit-tested); this only supplies the stops and moves
   * the focus. */
  function trapTab(event) {
    if (event.key !== 'Tab' || event.defaultPrevented === true) return;
    const stops = tabStops();
    if (stops.length === 0) return;
    const target = tabWrapTarget({
      shiftKey: event.shiftKey === true,
      activeIndex: stops.indexOf(document.activeElement),
      count: stops.length,
    });
    if (target === null) return;
    event.preventDefault();
    (target === 'first' ? stops[0] : stops[stops.length - 1]).focus();
  }

  // ───────────────────────────────────────────────────────────────── commit

  /**
   * Review 2026-07-29, findings #1–#3 — commit() and open() share this
   * closure across awaits, so two fences guard the lifecycle:
   *
   *   `committing` — true for exactly the life of commit()'s converge loop.
   *   Cart writes cannot be aborted once fired, so while they are landing
   *   every close request is refused out loud (`closeRequestAction`), the
   *   close button is disabled, open() is a no-op, and a close forced past
   *   all of that is resurrected (`shouldResurrect`). The alternative was a
   *   shopper choosing "Discard changes" while the loop was actively
   *   updating their cart, and a stall's warning painting into a closed
   *   dialog where nobody could see it.
   *
   *   `openGeneration` — a token minted by every open(). Work captured
   *   before an await re-checks it afterward and discards itself when a
   *   newer open owns the modal: an abandoned open's slow cart read can no
   *   longer repaint the current session (#3), and a commit that outlived
   *   its session through any door the fences above do not cover cannot
   *   paint its outcome over a newer one (#2).
   */
  let committing = false;
  let openGeneration = 0;

  async function commit() {
    // #1 — re-entrance guard in code, not just on the button: renderFooter
    // re-derives `el.submit.disabled` on every render, so a render arriving
    // mid-commit (eg-cart-reflect reacting to the loop's own writes) could
    // re-enable the button while the loop is still running.
    if (committing) return;
    const state = configurator.state();
    if (!state.canComplete) return;

    committing = true;
    const myGeneration = openGeneration;
    if (el.submit) el.submit.disabled = true;
    if (el.close) el.close.disabled = true;
    if (el.error) el.error.hidden = true;

    // R20 — ONE fresh id, minted HERE, before the loop. Minting inside would
    // hand every pass a different id and the loop would restamp forever.
    const bundleId = generateBundleId();

    // A fresh array of fresh literals: nothing the state module hands back is
    // forwarded into the destructive path by reference (obligation 6).
    const desiredLines = configurator.lines().map((line) => ({
      variantId: line.variantId,
      sellingPlanId: line.sellingPlanId,
      quantity: line.quantity,
    }));

    let result;
    try {
      result = await convergeCommit({
        rawMap,
        desiredLines,
        bundleId,
        readCart: readCartItems,
        applyOp: postCartOp,
        // R19 — the Kit may only be removed behind the shopper's own confirm,
        // which state records as `kitBroken` when `confirmBreak()` is called.
        confirmedBreak: state.kitBroken === true,
        // Obligation 3b / F8 — the ONE thing that makes a line missing from
        // this build an instruction to delete it. Captured at open from what
        // actually happened, never recomputed here.
        cartVouchedAtOpen,
        // Obligation 5 — the units hydration reported, not a recount.
        kitUnits: hydratedKitUnits,
        scopedLineCount: scopedLinesAtOpen,
        desiredSectionCount: state.sections.length + Object.keys(state.accessoryQuantities).length,
      });
    } catch (error) {
      console.error('[eg-configurator] commit threw', error);
      // #2 — a stale session's error must not repaint whoever owns the modal
      // now; the newer open's own render has already set these controls.
      if (myGeneration === openGeneration) {
        if (el.error) el.error.hidden = false;
        if (el.submit) el.submit.disabled = false;
      }
      return;
    } finally {
      committing = false;
      if (el.close) el.close.disabled = false;
    }

    // #2 — the session this commit belonged to is gone (a door the committing
    // fence does not cover, or a future regression in it); the outcome must
    // not paint over whichever session now owns the modal.
    if (myGeneration !== openGeneration) {
      console.warn('[eg-configurator] commit outlived its session — result withheld from the newer one');
      return;
    }

    // Obligation 3 and 9 — ONLY an OWN 'converged' may show the success screen.
    // Every other outcome routes to the partial-commit surface with local state
    // intact and no retry (AE16). The rule itself lives in `surfaceForResult`,
    // where it is unit-tested against absent, junk and INHERITED statuses.
    if (surfaceForResult(result) === 'confirmation') {
      // configurator_committed (2026-09-02) — the funnel's successful end.
      track('configurator_committed', { line_count: desiredLines.length });
      showSurface('confirmation', { arrivedByUrl });
      return;
    }
    partialCommitConfirm(result);
  }

  // ──────────────────────────────────────────────────────── open and close

  async function open({ viaUrl = false } = {}) {
    if (dialog.open) return;
    // #1 — a commit's writes are still landing; a new session must not start
    // over them (it could not vouch for the cart they are mid-way through
    // changing, and its build would race theirs).
    if (committing) return;
    // #3 — this open owns the modal until a newer open mints the next token;
    // everything after an await below re-checks it.
    const myGeneration = ++openGeneration;
    // F8 — DROP THE PREVIOUS OPEN'S VOUCH BEFORE ANYTHING CAN THROW. This is
    // set true only at the vouch point far below, so every early exit, every
    // exception in between and every second open starts from "we cannot vouch
    // for this cart" rather than inheriting a `true` earned by a different
    // cart, minutes ago, in a different modal session.
    cartVouchedAtOpen = false;
    // A new session starts from the default stage pick (Kyle, 2026-07-29 —
    // the persisted accessory circle is per-session, not forever).
    pickedAccessoryId = null;
    arrivedByUrl = viaUrl;
    dialog.showModal();
    document.body.classList.add('eg-modal-open');
    showSurface('build');
    if (el.error) el.error.hidden = true;

    // R14/R16/R17 — read the cart, hydrate, render. Clicks are local from here
    // until commit; nothing below touches the cart.
    //
    // THIS READ IS THE ONLY THING THAT MAKES A REMOVAL AT COMMIT LEGITIMATE, so
    // whether it succeeded is recorded rather than discarded (F8).
    //
    // The comment that used to sit here said a failed read was safe "because
    // commit re-reads anyway". THAT REASONING IS WRONG AND IT IS WHAT SHIPPED
    // THE BUG. Commit re-reads the CART. It cannot re-read the shopper's
    // INTENT, and intent is what a removal is derived from: the build below is
    // hydrated from `items`, so a failed read renders an EMPTY build, and an
    // empty build is byte-for-byte what a shopper who deliberately cleared
    // everything produces. Committed, that emptied a real cart of 1 Annual + 2
    // accessories in three writes at HTTP 200 and showed the success screen.
    //
    // What actually happens now: the modal still opens rather than blocking —
    // that part was right, a shopper mid-intent should not meet a dead end —
    // but the build it opens is marked ADDITIVE, so commit may only ADD. The
    // shopper is told at commit, on the partial-commit surface, in the same
    // register as every other stall.
    let items = [];
    let readSucceeded = false;
    try {
      items = await readCartItems();
      readSucceeded = true;
    } catch (error) {
      console.warn('[eg-configurator] cart unreadable at open — this build can only ADD (F8)', error);
    }

    // #3 — while that read was in flight the shopper closed and reopened; a
    // newer open owns the modal now and this continuation's cart read is
    // stale. Everything below paints, so none of it may run.
    if (myGeneration !== openGeneration) return;

    cartEmptyAtOpen = items.length === 0;
    scopedLinesAtOpen = countScopedLines(rawMap, items);

    let hydrated = null;
    try {
      hydrated = hydrate(rawMap, items);
    } catch (error) {
      console.warn('[eg-configurator] hydration failed — this build can only ADD (F8)', error);
      hydrated = null;
    }

    // THE SECOND ROUTE TO THE SAME HOLE, and it starts from a read that WORKED
    // (found while fixing F8). `createConfigurator(rawMap, { hydrated: null })`
    // renders the same empty build a blind open renders, so a `hydrate()` that
    // throws over a cart full of items would hand commit a build that removes
    // every one of them — the failed read is not the only way to get an
    // unearned empty build, it is just the way we found first.
    //
    // So the flag is not "did the fetch resolve". It is: CAN THIS SURFACE VOUCH
    // FOR WHAT IT PUT IN FRONT OF THE SHOPPER? Both halves have to hold — we
    // read the cart AND we turned it into the build they edited. Either one
    // missing and the build is ADDITIVE.
    cartVouchedAtOpen = readSucceeded && hydrated !== null;
    // configurator_opened (2026-09-02) — top-of-funnel intent into the
    // converge-commit loop. Fired here, not earlier: cart_vouched only means
    // something once the vouch flag above is settled.
    track('configurator_opened', {
      via_url: viaUrl,
      cart_vouched: cartVouchedAtOpen,
      scoped_line_count: scopedLinesAtOpen,
    });
    hydratedKitUnits = hydrated?.kit?.quantity ?? 0;
    readOnlyLines = hydrated?.readOnly ?? [];
    // KTD-D — displayed (U9's read-only note) and never editable. Nothing
    // downstream ever offers a control for these.
    renderReadOnly(readOnlyLines);

    configurator = createConfigurator(rawMap, { hydrated });
    const state = configurator.state();
    openSignature = buildSignature(state);

    const firstCover = state.sections.find((section) => section.kind === 'cover');
    const chosen = firstCover?.options.find((option) => option.selected) ?? null;
    // The dialog kept its DOM from the last open, so the stage key is cleared
    // to force a re-aim (thumbs, ring, step label).
    //
    // `paintedHeroKey` IS DELIBERATELY NOT CLEARED (Kyle, 2026-07-30 — THE
    // OPEN FLICKER). It used to be, to "re-fade even onto the same cover".
    // That re-fade is the flicker: clearing the key rebuilds the hero <img>,
    // the rebuilt node starts at opacity 0 under R5's entrance animation, and
    // for ~100ms the stage shows nothing but the heavily blurred backdrop copy
    // before the sharp photo climbs back in. Measured on the recording: sharp
    // at 0.15s, blur-only at 0.35s, sharp again by 0.45s.
    //
    // Keeping the key means same photo → same node → no re-fade → no gap. A
    // genuinely different cover (a cart hydrate that changes the selection)
    // still changes the key and still fades, which is what the fade is FOR.
    // The modal now has its own arrival animation, so the stage no longer
    // needs to announce the open a second time.
    paintedStageKey = null;
    focusStep('cover', chosen?.variantId ?? null, chosen ? `${chosen.coverTitle} — ${chosen.title}` : 'Your build');
    render(state);
  }

  /** D3 — set for exactly the moment a close THIS FILE asked for is in flight,
   * so the `close` backstop below can tell it apart from one nobody asked for.
   *
   * CONSUMED BY THE `close` LISTENER, NOT RESET HERE (Kyle, 2026-07-30). The
   * flag used to be cleared in a `finally` around `dialog.close()`, which is
   * wrong: `close()` QUEUES the `close` event rather than dispatching it
   * synchronously, so the `finally` always won the race and every deliberate
   * close arrived at the backstop looking accidental. With a dirty build that
   * made "Discard changes" re-raise the modal it had just dismissed — the
   * shopper could not get out. The listener now reads the flag and clears it,
   * so it survives exactly as long as the close it describes. */
  let closingDeliberately = false;

  function closeDialog() {
    // A dialog that is not open fires no `close` event, so nothing would ever
    // consume the flag and the NEXT unasked-for close would be waved through.
    if (!dialog.open) return;
    closingDeliberately = true;
    try {
      // Not `dialog.close()` — see eg-modal-exit.js. The real close is deferred
      // until the exit fade finishes, which means `closingDeliberately` now has
      // to survive that wait too. It already does: it is cleared by the `close`
      // listener that consumes it, not on a timer.
      closeModalWithExit(dialog);
    } catch (error) {
      closingDeliberately = false;
      throw error;
    }
  }

  /** R2 — closing depends on ARRIVAL: a URL arrival has no page behind the
   * modal worth returning to and is sent to the exit named on the shell; a CTA
   * arrival simply closes back onto the page they were on. */
  function finishClose() {
    closeDialog();
    if (arrivedByUrl && urlArrivalExit) window.location.assign(urlArrivalExit);
  }

  /** #1 — the refusal, said where a screen reader hears it. The close button
   * is also disabled for the duration (commit()), so for most shoppers this
   * line answers only the Escape key. */
  function refuseCloseWhileCommitting() {
    if (el.announce) el.announce.textContent = 'Updating your cart — one moment.';
  }

  function requestClose() {
    // #1 — writes are landing; the only honest answer is "not yet". Guarded
    // here as well as in routeCloseRequest because el.close's click handler
    // enters through this door directly.
    if (committing) {
      refuseCloseWhileCommitting();
      return;
    }
    // R15 — the discard prompt, in the register keyed on whether the cart was
    // empty AT OPEN. Closing with NO local changes must not prompt at all.
    if (isDirty()) {
      discardConfirm({ cartEmptyAtOpen, onDiscard: finishClose });
      return;
    }
    finishClose();
  }

  /** D3 — one door for every close request, whichever event carried it, so the
   * keydown path and the `cancel` path can never drift apart. */
  function routeCloseRequest() {
    const action = closeRequestAction(surface, { committing });
    if (action === 'refuse-committing') {
      refuseCloseWhileCommitting();
      return;
    }
    if (action === 'decline-overlay') {
      // Declining the Kit break, keeping the build, acknowledging the stall —
      // whichever this overlay's secondary offers.
      const decline = el.confirmSecondary && !el.confirmSecondary.hidden ? el.confirmSecondary : null;
      if (decline) decline.click();
      else closeConfirm();
      return;
    }
    if (action === 'exit-confirmation') {
      finishClose();
      return;
    }
    requestClose();
  }

  dialog.addEventListener('close', () => {
    // Read AND clear: `closeDialog` sets this and deliberately does not reset
    // it, because the event it describes arrives on a later task.
    const deliberate = closingDeliberately;
    closingDeliberately = false;

    document.body.classList.remove('eg-modal-open');
    // Leaving the build inert would make the modal unusable if it is reopened.
    if (el.viewBuild) el.viewBuild.inert = false;

    // D3's BACKSTOP — see `shouldResurrect`. A close nobody asked for, with an
    // unanswered question up or a build that has moved since it opened, is a
    // shopper losing visible work; put the modal straight back rather than let
    // it go. Never reached on any ordinary path, because every close this file
    // makes goes through `closeDialog`.
    if (!shouldResurrect({ deliberate, surface, dirty: isDirty(), committing })) return;
    try {
      dialog.showModal();
      document.body.classList.add('eg-modal-open');
      if (el.viewBuild) el.viewBuild.inert = OVERLAY_SURFACES.includes(surface);
      const target = el.confirm && !el.confirm.hidden ? el.confirm.querySelector('button:not([hidden])') : el.close;
      if (target && typeof target.focus === 'function') target.focus();
    } catch (error) {
      console.warn('[eg-configurator] could not re-raise the modal after an unasked-for close', error);
    }
  });

  // D3 — ESCAPE IS TAKEN HERE, NOT ON `cancel`; see `takeEscapeKeydown` for why
  // `cancel` cannot be trusted to carry it. On `document` rather than on the
  // dialog because Chrome's own wrap can leave `document.activeElement` on
  // `<body>` (D4), and a keydown there would never bubble through the dialog.
  /** Set while a close request taken on the keydown is being handled, so a UA
   * that fires `cancel` anyway cannot route the SAME press twice — declining an
   * overlay and then immediately re-prompting is its own lost-work bug. */
  let escapeTakenOnKeydown = false;

  document.addEventListener(
    'keydown',
    (event) => {
      if (!dialog.open) return;
      if (takeEscapeKeydown(event, { open: true })) {
        escapeTakenOnKeydown = true;
        setTimeout(() => {
          escapeTakenOnKeydown = false;
        }, 0);
        routeCloseRequest();
        return;
      }
      trapTab(event);
    },
    true
  );

  dialog.addEventListener('cancel', (event) => {
    // Belt to the keydown's braces: a close request that arrives by some other
    // route still routes through the same door. A non-cancelable `cancel` is
    // beyond stopping here — that is what `takeEscapeKeydown` and the `close`
    // backstop exist for — so this only ever asks politely.
    if (event.cancelable === true) event.preventDefault();
    if (escapeTakenOnKeydown) return;
    routeCloseRequest();
  });
  if (el.close) el.close.addEventListener('click', requestClose);
  if (el.submit) el.submit.addEventListener('click', commit);

  // R2 — openable from CTAs site-wide (the existing contract, unchanged).
  for (const trigger of document.querySelectorAll(OPEN_TRIGGER)) {
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      open({ viaUrl: false });
    });
  }

  render(configurator.state());

  // R2 — the build page renders the autoopen marker; arriving by URL opens the
  // modal immediately and remembers that the exit is a route, not a return.
  if (document.querySelector(AUTOOPEN_MARKER)) open({ viaUrl: true });
}

// The loop above is imported and tested in a DOM-free environment, so the
// bootstrap only runs where there is a document to bootstrap into.
if (typeof document !== 'undefined') {
  document.querySelectorAll(SELECTORS.root).forEach(initConfigurator);
}
